// Admin-only sidebar panel with four tabs: Groups & Topics, Users (role +
// ban controls), Redemptions (cashback fulfillment queue), Tokens (service
// token management). Mounted in App.jsx as the "Admin" sidebar tab.
import { useState, useEffect, useRef, useMemo } from "react";
import { api } from "../api.js";
import { T, T_NEW } from "../theme.js";
import { I } from "../icons.jsx";
import { Av, Btn, Inp, Tabs, Mono, Serif } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT } from "../i18n.jsx";
import { AdminCuratedPanel } from "./AdminCuratedPanel.jsx";
import { AdminHome } from "./AdminHome.jsx";
import { BatchesPanel } from "./BatchesPanel.jsx";

export function AdminPanel({ user, users, groups, onRefresh, onCreateGroup, onAddTopic, initialTab, onOpenDm }) {
  const toast = useToast();
  const t = useT();
  // initialTab lets the (now-embedded) AdminHome card-cards deep-link
  // a specific section. Default to "overview" — the AdminHome content
  // is now AdminPanel's first tab so there's a single surface, no more
  // home-vs-panel redundancy.
  const [tab, setTab] = useState(initialTab || "overview");
  const [pendingReds, setPendingReds] = useState([]);
  // Subscription orders queue (migration 0039). Admin reviews,
  // approves out-of-band payment, and grants premium. Empty when no
  // resellers have filed an in-app order yet.
  const [pendingSubs, setPendingSubs] = useState([]);
  const [allSubs, setAllSubs] = useState([]);
  // Reseller application queue (migration 0052). Holds the
  // 'submitted' state only — 'pending' is "user hasn't filled the
  // form yet" and isn't reviewable. Decisions flip to approved/rejected
  // which drops the row from this list.
  const [pendingApps, setPendingApps] = useState([]);
  const [appFilter, setAppFilter] = useState("submitted");
  const [appRejectFor, setAppRejectFor] = useState(null);
  const [appRejectNote, setAppRejectNote] = useState("");
  // These two are referenced by the panel's redemption row state; left in
  // place even though the inline note input was simplified to window.prompt
  // — keeps the API stable if we re-introduce an inline UI later.
  const [, setFulfillNoteFor] = useState(null);
  const [, setFulfillNote] = useState("");
  // Silence unused-warning lint without removing the setters above
  void setFulfillNoteFor; void setFulfillNote;

  const [tokens, setTokens] = useState([]);
  const [newTokenPlain, setNewTokenPlain] = useState(null); // shown once after creation
  const [tokenName, setTokenName] = useState("");
  const [tokenScopes, setTokenScopes] = useState("posts.write");

  // Audit log tab state
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditFilter, setAuditFilter] = useState({ action: "", targetType: "" });
  const [auditLoading, setAuditLoading] = useState(false);

  // Orders pipeline tab state
  const [orders, setOrders] = useState([]);
  const [ordersFilter, setOrdersFilter] = useState({ status: "" });
  const [ordersLoading, setOrdersLoading] = useState(false);
  const ORDER_STATUSES = ["placed", "confirmed", "paid", "purchased", "received", "shipped", "delivered", "cancelled", "unavailable"];

  // Shipments tab state
  const [shipments, setShipments] = useState([]);
  const [shipmentsFilter, setShipmentsFilter] = useState({ status: "" });
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const loadShipments = async () => {
    setShipmentsLoading(true);
    try {
      const params = {};
      if (shipmentsFilter.status) params.status = shipmentsFilter.status;
      setShipments(await api.getAdminShipments(params));
    } catch (e) { toast.error(e.message); }
    finally { setShipmentsLoading(false); }
  };
  useEffect(() => { if (tab === "shipments") loadShipments(); }, [tab, shipmentsFilter.status]);
  const SHIPMENT_STATUSES = ["building", "ready", "shipped", "delivered", "cancelled"];

  // FX settings tab state
  const [fx, setFx] = useState(null);                // { base, markupPct, effective } or null
  const [fxBaseInput, setFxBaseInput] = useState(""); // string for the text field
  const [fxMarkupInput, setFxMarkupInput] = useState("");
  const [fxSaving, setFxSaving] = useState(false);
  const loadFx = async () => {
    try {
      const s = await api.getFxSettings();
      setFx(s);
      setFxBaseInput(String(s.base));
      setFxMarkupInput(String(s.markupPct));
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { if (tab === "settings") loadFx(); }, [tab]);
  // Live preview of what the effective rate WOULD be if the unsaved
  // inputs were applied. base * (1 + markup/100). Falls back to "—" if
  // either input isn't a valid number yet.
  const previewEffective = (() => {
    const b = parseFloat(fxBaseInput);
    const m = parseFloat(fxMarkupInput);
    if (!isFinite(b) || b <= 0 || !isFinite(m) || m < 0) return null;
    return b * (1 + m / 100);
  })();
  const saveFx = async () => {
    const b = parseFloat(fxBaseInput);
    const m = parseFloat(fxMarkupInput);
    if (!isFinite(b) || b <= 0) { toast.error(t("admin.fx.error.base")); return; }
    if (!isFinite(m) || m < 0) { toast.error(t("admin.fx.error.markup")); return; }
    setFxSaving(true);
    try {
      const next = await api.setFxSettings({ base: b, markupPct: m });
      setFx(next);
      setFxBaseInput(String(next.base));
      setFxMarkupInput(String(next.markupPct));
      toast.success(t("admin.fx.saved"));
    } catch (e) { toast.error(e.message); }
    finally { setFxSaving(false); }
  };

  // ─── FX auto-updater (CBR hourly poll) ───
  // Manual refresh pulls a fresh CBR rate immediately (writes the new
  // base on change, then surfaces the updated state). Toggle flips the
  // hourly loop on/off without restarting the API.
  const [fxRefreshing, setFxRefreshing] = useState(false);
  const refreshFx = async () => {
    setFxRefreshing(true);
    try {
      const next = await api.refreshFxFromCbr();
      setFx(next);
      setFxBaseInput(String(next.base));
      setFxMarkupInput(String(next.markupPct));
      if (next.auto?.lastStatus === "ok") {
        toast.success(t("admin.fx.auto.cbrToast", { rate: next.auto.lastSourceRate }));
      } else {
        toast.error(t("admin.fx.auto.cbrFailed", { err: next.auto?.lastError ?? t("admin.fx.auto.status.unknown") }));
      }
    } catch (e) { toast.error(e.message); }
    finally { setFxRefreshing(false); }
  };
  const toggleFxAuto = async (enabled) => {
    try {
      const next = await api.setFxAutoEnabled(enabled);
      setFx((cur) => ({ ...(cur ?? {}), auto: next.auto }));
      toast.success(enabled ? t("admin.fx.auto.enabled") : t("admin.fx.auto.disabled"));
    } catch (e) { toast.error(e.message); }
  };

  // ─── Payment methods (Feature 1) ───
  // Single editable form whose state mirrors the PaymentMethods JSON in
  // the settings table. Loaded lazily when the Settings tab opens.
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [pmSaving, setPmSaving] = useState(false);
  const loadPaymentMethods = async () => {
    try { setPaymentMethods(await api.getAdminPaymentMethods()); }
    catch (e) { toast.error(e.message); }
  };
  useEffect(() => { if (tab === "settings") loadPaymentMethods(); }, [tab]);
  const updatePmField = (k, v) => setPaymentMethods((cur) => ({ ...(cur ?? {}), [k]: v }));
  const savePaymentMethods = async () => {
    if (!paymentMethods) return;
    setPmSaving(true);
    try {
      const next = await api.setAdminPaymentMethods(paymentMethods);
      setPaymentMethods(next);
      toast.success(t("admin.pm.saved"));
    } catch (e) { toast.error(e.message); }
    finally { setPmSaving(false); }
  };

  const loadReds = async () => {
    try { setPendingReds(await api.getRedemptionQueue("pending")); } catch (e) { console.error(e); }
  };
  const loadSubs = async () => {
    try {
      const all = await api.listSubscriptionOrders();
      setAllSubs(all || []);
      setPendingSubs((all || []).filter((o) => o.status === "pending"));
    } catch (e) { console.error(e); }
  };
  const loadTokens = async () => {
    try { setTokens(await api.getServiceTokens()); } catch (e) { console.error(e); }
  };
  const loadApps = async () => {
    try { setPendingApps(await api.adminListApplications(appFilter)); }
    catch (e) { console.error(e); }
  };
  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const params = { limit: "200" };
      if (auditFilter.action) params.action = auditFilter.action;
      if (auditFilter.targetType) params.targetType = auditFilter.targetType;
      setAuditEntries(await api.getAuditLog(params));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAuditLoading(false);
    }
  };
  useEffect(() => { if (tab === "redemptions") loadReds(); }, [tab]);
  useEffect(() => { if (tab === "subscriptions") loadSubs(); }, [tab]);
  useEffect(() => { if (tab === "applications") loadApps(); }, [tab, appFilter]);
  useEffect(() => { loadApps(); }, []); // initial badge fill
  // Also fetch on mount so the badge count on the tab label is
  // accurate before the admin clicks the tab.
  useEffect(() => { loadSubs(); }, []);
  useEffect(() => { if (tab === "tokens") loadTokens(); }, [tab]);

  // ─── Channels tab — fee + cashback editor ───
  // Loaded lazily when the tab opens. Inline-editable; per-row Save
  // posts the fee/cashback patch to PATCH /api/channels/:id.
  const [adminChannels, setAdminChannels] = useState([]);
  const loadAdminChannels = async () => {
    try { setAdminChannels(await api.getChannels()); }
    catch (e) { toast.error(e.message); }
  };
  useEffect(() => { if (tab === "channels") loadAdminChannels(); }, [tab]);

  // ─── WhatsApp ingest tab — sender routes + recent unmapped senders ───
  const [waRoutes, setWaRoutes] = useState([]);
  const [waRecent, setWaRecent] = useState([]);
  const loadWaData = async () => {
    try {
      const [r, recent] = await Promise.all([api.getWaRoutes(), api.getWaRecentSenders()]);
      setWaRoutes(r); setWaRecent(recent);
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { if (tab === "wa") loadWaData(); }, [tab]);

  // ─── DM templates tab (Feature 6) ───
  const [templates, setTemplates] = useState([]);
  const loadTemplates = async () => {
    try { setTemplates(await api.getDmTemplates()); } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { if (tab === "templates") loadTemplates(); }, [tab]);
  const createTemplate = async () => {
    const name = await toast.prompt(t("admin.templates.create.namePrompt"), { placeholder: t("admin.templates.create.namePlaceholder") });
    if (!name) return;
    const ru = await toast.prompt(t("admin.templates.create.bodyPrompt"), {
      placeholder: t("admin.templates.create.bodyPlaceholder"), multiline: true,
    });
    if (ru === null) return;
    try {
      await api.createDmTemplate({ name, bodyRu: ru, bodyEn: "", ord: 100 });
      await loadTemplates();
      toast.success(t("admin.templates.create.ok"));
    } catch (e) { toast.error(e.message); }
  };
  const deleteTemplate = async (id, name) => {
    if (!await toast.confirm(t("admin.templates.delete.confirm", { name }))) return;
    try { await api.deleteDmTemplate(id); await loadTemplates(); toast.success(t("admin.templates.delete.ok")); }
    catch (e) { toast.error(e.message); }
  };
  // Reload audit log whenever the tab is opened or filters change
  useEffect(() => { if (tab === "audit") loadAudit(); }, [tab, auditFilter.action, auditFilter.targetType]);

  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      const params = { limit: "200" };
      if (ordersFilter.status) params.status = ordersFilter.status;
      setOrders(await api.getAdminOrders(params));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setOrdersLoading(false);
    }
  };
  useEffect(() => { if (tab === "orders") loadOrders(); }, [tab, ordersFilter.status]);

  const advanceOrder = async (id, status, cancelReason, qtyFulfilled) => {
    try {
      await api.updateOrderStatus(id, status, cancelReason, qtyFulfilled);
      await loadOrders();
    } catch (e) { toast.error(e.message); }
  };

  const fmt = (cents) => (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

  const createToken = async () => {
    if (!tokenName.trim()) return;
    try {
      const r = await api.createServiceToken(
        tokenName.trim(),
        tokenScopes.split(",").map(s => s.trim()).filter(Boolean),
      );
      setNewTokenPlain(r.token);
      setTokenName("");
      await loadTokens();
    } catch (e) { toast.error(e.message); }
  };

  return (
    // Cap the admin panel at editorial-density width — when this
    // component used to live inside a 520px modal nothing capped it.
    // Now that we mount it inline at full window width we need our own
    // constraint so tabs, queue cards, and system grid stop sprawling.
    <div style={{ maxWidth: "min(1024px, 100vw)", margin: "0 auto",
                  width: "100%",
                  // Hard nuclear cap. 100vw uses the actual viewport
                  // width regardless of any parent constraint, so the
                  // Tabs row's min-content (~744px from 7 buttons)
                  // can't drag the panel past the screen edge on
                  // iPhone. minWidth:0 + overflowX:hidden are belt
                  // and suspenders. position:relative makes sure
                  // iOS Safari treats overflow-x:hidden the way the
                  // spec intends.
                  minWidth: 0,
                  overflowX: "hidden",
                  position: "relative",
                  boxSizing: "border-box" }}>
      {/* Top-level tabs limited to daily-action items. The rare-use
          surfaces (Channels / Users / Groups / Audit / Tokens /
          Templates / WhatsApp / Redemptions) sit inside the Settings
          tab as a sub-nav so the strip stays scannable. The active
          tab below remaps any "system" child id (users, audit, …) to
          show the Settings hub instead. */}
      {(() => {
        const SYSTEM_TABS = new Set([
          "settings", "groups", "users", "channels", "redemptions",
          "wa", "tokens", "templates", "audit",
        ]);
        const topActive = SYSTEM_TABS.has(tab) ? "settings" : tab;
        return (
          <Tabs
            tabs={[
              { id: "overview",      label: t("admin.tab.overview") },
              { id: "applications",  label: `${t("admin.tab.applications")}${pendingApps.length ? ` (${pendingApps.length})` : ""}` },
              { id: "curated",       label: t("admin.tab.curated") },
              { id: "subscriptions", label: `${t("admin.tab.subscriptions")}${pendingSubs.length ? ` (${pendingSubs.length})` : ""}` },
              { id: "orders",        label: t("admin.tab.orders") },
              { id: "batches",       label: t("admin.tab.batches") },
              { id: "shipments",     label: t("admin.tab.shipments") },
              { id: "settings",      label: t("admin.tab.settings") },
            ]}
            active={topActive}
            onSelect={setTab}
          />
        );
      })()}
      {/* Sub-nav for the "Настройки" hub. Only renders when the
          active tab is one of the system surfaces. Click any pill
          to set the panel's tab. */}
      {["settings", "groups", "users", "channels", "redemptions",
        "wa", "tokens", "templates", "audit"].includes(tab) && (
        <div style={{ display: "flex", gap: 6, padding: "10px 0 14px",
                      flexWrap: "wrap", borderBottom: `1px solid ${T_NEW.ink12}`,
                      marginBottom: 14 }}>
          {[
            ["settings",    t("admin.sub.payments")],
            ["channels",    t("admin.sub.channels")],
            ["users",       `${t("admin.sub.users")}${users.length ? ` (${users.length})` : ""}`],
            ["groups",      t("admin.sub.groups")],
            ["wa",          t("admin.sub.wa")],
            ["tokens",      t("admin.sub.tokens")],
            ["templates",   t("admin.sub.templates")],
            ["redemptions", `${t("admin.sub.redemptions")}${pendingReds.length ? ` (${pendingReds.length})` : ""}`],
            ["audit",       t("admin.sub.audit")],
          ].map(([k, label]) => {
            const active = tab === k;
            return (
              <button key={k} type="button" onClick={() => setTab(k)}
                      style={{ padding: "6px 12px",
                               border: `1px solid ${active ? T_NEW.ink : T_NEW.ink12}`,
                               background: active ? T_NEW.ink : T_NEW.paper,
                               color: active ? T_NEW.paper : T_NEW.ink,
                               borderRadius: 999, fontSize: 12,
                               fontFamily: T_NEW.fontSans, fontWeight: 500,
                               cursor: "pointer" }}>
                {label}
              </button>
            );
          })}
        </div>
      )}
      {tab === "overview" && (
        <AdminHome user={user} onOpenAdminTab={setTab} />
      )}
      {tab === "batches" && (
        <BatchesPanel scope="facilitated" />
      )}
      {tab === "orders" && (
        <div>
          <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
            <select value={ordersFilter.status}
                    onChange={(e) => setOrdersFilter(f => ({ ...f, status: e.target.value }))}
                    style={{ flex: 1, minWidth: 160, background: T_NEW.paper, color: T_NEW.ink,
                             border: `1px solid ${T_NEW.ink12}`, padding: "10px 12px",
                             borderRadius: T_NEW.r1, fontSize: 13, fontFamily: T_NEW.fontSans }}>
              <option value="">{t("admin.orders.filter.all")}</option>
              {ORDER_STATUSES.map(s => <option key={s} value={s}>{t(`admin.orders.status.${s}`)}</option>)}
            </select>
            <button onClick={loadOrders} disabled={ordersLoading}
                    style={{ background: T_NEW.persimmonSoft, color: T_NEW.persimmon, border: "none",
                             cursor: "pointer", padding: "8px 16px", borderRadius: T_NEW.r1,
                             fontSize: T_NEW.micro, fontWeight: 700, fontFamily: T_NEW.fontMono,
                             textTransform: "uppercase", letterSpacing: T_NEW.monoTrack }}>
              {ordersLoading ? t("admin.orders.refreshing") : t("admin.orders.refresh")}
            </button>
          </div>

          {orders.length === 0 && !ordersLoading ? (
            <div style={{ color: T_NEW.ink56, fontSize: T_NEW.bodySm, padding: 28, textAlign: "center",
                          background: T_NEW.paperSoft, borderRadius: T_NEW.r1,
                          fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
              {t("admin.orders.empty")}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {orders.map((o) => (
                <div key={o.id} style={{ background: T_NEW.paperSoft, padding: 14,
                                          borderRadius: T_NEW.r2, border: `1px solid ${T_NEW.ink12}` }}>
                  {o.orderNumber && (
                    <div style={{ fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro,
                                  color: T_NEW.persimmon, fontWeight: 700, marginBottom: 8,
                                  letterSpacing: "0.02em" }}>
                      № {o.orderNumber}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
                    {o.post?.imageUrl && (
                      <img src={o.post.imageUrl} alt=""
                           onClick={() => window.open(o.post.imageUrl, "_blank", "noopener")}
                           style={{ width: 56, height: 56, objectFit: "cover",
                                    borderRadius: T_NEW.r1, flexShrink: 0, cursor: "zoom-in" }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: T_NEW.fontSerif, fontSize: T_NEW.bodySm, fontWeight: 500,
                                    color: T_NEW.ink, lineHeight: 1.25,
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {o.post?.title ?? t("admin.orders.deletedPost")}
                      </div>
                      <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 4,
                                    fontFamily: T_NEW.fontMono }}>
                        {o.customer?.displayName} · {o.customer?.email}
                      </div>
                      <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, marginTop: 4,
                                    fontFamily: T_NEW.fontSans }}>
                        {o.variant ? `${o.variant} · ` : ""}{t("admin.orders.qtyShort", { n: o.qty })}
                        {o.qtyFulfilled != null && o.qtyFulfilled !== o.qty && (
                          <span style={{ color: T_NEW.danger, fontWeight: 700 }}> {t("admin.orders.fulfilledShort", { n: o.qtyFulfilled })}</span>
                        )}
                        {o.notes && (
                          <span style={{ fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
                            {` · «${o.notes}»`}
                          </span>
                        )}
                      </div>
                      {o.recipientName && (
                        <div style={{ fontSize: T_NEW.micro, color: T_NEW.persimmon, marginTop: 6,
                                      whiteSpace: "pre-wrap", fontFamily: T_NEW.fontSans }}>
                          → {o.recipientName}{o.recipientPhone ? ` · ${o.recipientPhone}` : ""}
                          {o.shipAddress ? `\n${o.shipAddress}` : ""}
                          {o.shipCountry ? `, ${o.shipCountry}` : ""}
                        </div>
                      )}
                      {/* Where to buy (direct link), store item #, BG code, and
                          a one-tap chat with the customer — admin fulfillment. */}
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center",
                                    gap: 10, marginTop: 8 }}>
                        {o.post?.sourceUrl && (
                          <a href={o.post.sourceUrl} target="_blank" rel="noopener noreferrer"
                             style={{ fontSize: T_NEW.micro, color: T_NEW.persimmon,
                                      fontFamily: T_NEW.fontMono, textDecoration: "none", fontWeight: 600 }}>
                            {t("admin.orders.source")}: {o.post.source || "store"} ↗
                          </a>
                        )}
                        {o.post?.itemNumber && (
                          <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, fontFamily: T_NEW.fontMono }}>
                            {t("admin.orders.itemNo")}: {o.post.itemNumber}
                          </span>
                        )}
                        {o.post?.publicCode && (
                          <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, fontFamily: T_NEW.fontMono }}>
                            {o.post.publicCode}
                          </span>
                        )}
                        {o.customer?.uid && (
                          <button type="button"
                                  onClick={async () => {
                                    try {
                                      const r = await api.createDm(o.customer.uid);
                                      if (onOpenDm) onOpenDm(r.id);
                                      else toast.success(t("admin.orders.chatOpened"));
                                    } catch (e) { toast.error(e.message || String(e)); }
                                  }}
                                  style={{ fontSize: T_NEW.micro, fontFamily: T_NEW.fontMono,
                                           cursor: "pointer", border: `1px solid ${T_NEW.ink12}`,
                                           borderRadius: 6, padding: "2px 8px", background: "transparent",
                                           color: T_NEW.ink }}>
                            💬 {t("admin.orders.chat")}
                          </button>
                        )}
                      </div>
                      {/* Extra product photos — tap to enlarge in a new tab. */}
                      {o.post?.imageUrls?.length > 1 && (
                        <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
                          {o.post.imageUrls.map((u, i) => (
                            <img key={i} src={u} alt=""
                                 onClick={() => window.open(u, "_blank", "noopener")}
                                 style={{ width: 40, height: 40, objectFit: "cover",
                                          borderRadius: 4, cursor: "zoom-in" }} />
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <Serif size={T_NEW.bodyLg} color={T_NEW.ink} style={{ display: "block", lineHeight: 1 }}>
                        ${(o.totalUsdCents / 100).toFixed(2)}
                      </Serif>
                      <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 4,
                                    fontFamily: T_NEW.fontMono }}>
                        ≈ ₽{(o.totalRubCents / 100).toFixed(0)}
                      </div>
                    </div>
                  </div>
                  {/* Payment-proof badge: shows on any order where the
                      customer uploaded a screenshot. Tap to view in a
                      new tab; once operator flips status past 'confirmed'
                      this remains visible as historical record. */}
                  {o.paymentScreenshotUrl && (
                    <div style={{ marginBottom: 10, padding: "8px 12px",
                                  background: o.status === "confirmed" ? `${T_NEW.danger}14` : `${T_NEW.bottle}14`,
                                  border: `1px solid ${o.status === "confirmed" ? T_NEW.danger : T_NEW.bottle}33`,
                                  borderRadius: T_NEW.r1, fontSize: T_NEW.micro, display: "flex",
                                  gap: 10, alignItems: "center", fontFamily: T_NEW.fontMono,
                                  textTransform: "uppercase", letterSpacing: T_NEW.monoTrack,
                                  color: o.status === "confirmed" ? T_NEW.danger : T_NEW.ink }}>
                      <span style={{ fontWeight: 700 }}>
                        {o.status === "confirmed" ? t("admin.orders.paymentBadge.needReview") : t("admin.orders.paymentBadge.uploaded")}
                      </span>
                      <span style={{ color: T_NEW.ink56, fontWeight: 600 }}>
                        {o.paymentMethodChosen}
                      </span>
                      <a href={o.paymentScreenshotUrl} target="_blank" rel="noopener noreferrer"
                         style={{ marginLeft: "auto", color: T_NEW.persimmon, fontWeight: 700,
                                  textDecoration: "underline" }}>
                        {t("admin.orders.paymentBadge.open")}
                      </a>
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: T_NEW.micro,
                                   color: o.status === "cancelled" ? T_NEW.danger : T_NEW.persimmon,
                                   fontWeight: 700, textTransform: "uppercase",
                                   letterSpacing: T_NEW.monoTrack, fontFamily: T_NEW.fontMono }}>
                      {o.status}
                    </span>
                    <select
                      value={o.status}
                      onChange={async (e) => {
                        const next = e.target.value;
                        if (next === "cancelled" || next === "unavailable") {
                          const placeholder = next === "unavailable"
                            ? t("admin.orders.unavailable.placeholder")
                            : t("admin.orders.cancel.placeholder");
                          const title = next === "unavailable"
                            ? t("admin.orders.unavailable.reasonTitle")
                            : t("admin.orders.cancel.reasonTitle");
                          const reason = await toast.prompt(title, { placeholder, multiline: true });
                          if (reason === null) { e.target.value = o.status; return; }
                          await advanceOrder(o.id, next, reason || null);
                        } else if (next === "received") {
                          // Encourage (not enforce) a proof photo when
                          // marking received. Transition first, then
                          // open the upload widget the user just
                          // revealed by changing status.
                          await advanceOrder(o.id, next);
                          toast.success(t("admin.orders.received.ok"));
                          return;
                        } else if (next === "purchased") {
                          // Ask how many were actually bought. Default to
                          // full qty; 0 → server flips to 'unavailable'.
                          const ans = await toast.prompt(
                            t("admin.orders.purchased.prompt", { qty: o.qty }),
                            { placeholder: String(o.qty), defaultValue: String(o.qty) },
                          );
                          if (ans === null) { e.target.value = o.status; return; }
                          const n = parseInt(ans, 10);
                          if (!Number.isFinite(n) || n < 0 || n > o.qty) {
                            toast.error(t("admin.orders.purchased.error", { qty: o.qty }));
                            e.target.value = o.status;
                            return;
                          }
                          await advanceOrder(o.id, next, null, n);
                        } else {
                          await advanceOrder(o.id, next);
                        }
                      }}
                      style={{ background: T.sidebar, color: T.text, border: `1px solid ${T.accent}40`, padding: "4px 8px", borderRadius: 6, fontSize: 11, cursor: "pointer" }}
                    >
                      {ORDER_STATUSES.map(s => <option key={s} value={s}>{t(`admin.orders.status.${s}`)}</option>)}
                    </select>
                    <span style={{ fontSize: 10, color: T.textSec, marginLeft: "auto" }}>
                      {new Date(o.placedAt * 1000).toLocaleString()}
                    </span>
                  </div>
                  {o.cancelReason && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{t("admin.orders.reasonLabel")} {o.cancelReason}</div>}
                  {/* Receipt-photo strip (Feature 4). Visible for any
                      order at 'received' or beyond; admin can add more
                      via the inline file input. */}
                  {["received", "shipped", "delivered"].includes(o.status) && (
                    <ReceiptPhotos order={o} onChanged={loadOrders} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {tab === "applications" && (
        <div>
          {/* Application gate review queue. Default filter is
              'submitted' since that's the actionable bucket. Switch
              filters to retroactively review approved/rejected
              decisions or to see who hasn't submitted yet. */}
          <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
            <select value={appFilter}
                    onChange={(e) => setAppFilter(e.target.value)}
                    style={{ padding: "8px 10px", borderRadius: 6,
                             border: `1px solid ${T_NEW.ink12}` }}>
              <option value="submitted">{t("admin.apps.filter.submitted")}</option>
              <option value="pending">{t("admin.apps.filter.pending")}</option>
              <option value="approved">{t("admin.apps.filter.approved")}</option>
              <option value="rejected">{t("admin.apps.filter.rejected")}</option>
            </select>
            <Btn variant="ghost" onClick={loadApps}>{t("admin.apps.refresh")}</Btn>
            <Mono color={T_NEW.ink56} size={T_NEW.micro}>
              {pendingApps.length} {appFilter === "submitted" ? t("admin.apps.count.submitted") : t("admin.apps.count.other")}
            </Mono>
          </div>
          {pendingApps.length === 0 ? (
            <Serif size={14} color={T_NEW.ink56} style={{ padding: 20 }}>
              {t("admin.apps.empty")}
            </Serif>
          ) : pendingApps.map((a) => (
            <div key={a.uid} style={{
              padding: 14, marginBottom: 12,
              border: `1px solid ${T_NEW.ink12}`,
              borderRadius: 8, background: T_NEW.paperRaised,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <Mono size={14} style={{ fontWeight: 700 }}>{a.displayName || a.email}</Mono>
                  <div style={{ fontSize: 12, color: T_NEW.ink56, marginTop: 2 }}>
                    {a.email}{a.nickname ? ` · @${a.nickname}` : ""}{a.phone ? ` · ${a.phone}` : ""}
                  </div>
                  {a.submittedAt && (
                    <div style={{ fontSize: 11, color: T_NEW.ink38, marginTop: 4 }}>
                      {t("admin.apps.submittedAt", { when: new Date(a.submittedAt * 1000).toLocaleString() })}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right" }}>
                  <Mono size={13} style={{ letterSpacing: 1.2, fontWeight: 700 }}>{a.code}</Mono>
                  <div style={{ fontSize: 11, color: T_NEW.ink56 }}>{t("admin.apps.verificationCode")}</div>
                </div>
              </div>
              {a.proof && (
                <div style={{ marginTop: 12, padding: 12,
                              background: T_NEW.paperSoft, borderRadius: 6,
                              display: "grid", gridTemplateColumns: "140px 1fr",
                              gap: "6px 12px", fontSize: 13 }}>
                  <div style={{ color: T_NEW.ink56 }}>{t("admin.apps.field.channel")}</div>
                  <a href={a.proof.channelUrl} target="_blank" rel="noopener noreferrer"
                     style={{ color: T_NEW.accent, wordBreak: "break-all" }}>
                    {a.proof.channelUrl}
                  </a>
                  <div style={{ color: T_NEW.ink56 }}>{t("admin.apps.field.platform")}</div>
                  <div>{a.proof.platform || "—"}</div>
                  <div style={{ color: T_NEW.ink56 }}>{t("admin.apps.field.audience")}</div>
                  <div>{a.proof.audienceBand}</div>
                  <div style={{ color: T_NEW.ink56 }}>{t("admin.apps.field.categories")}</div>
                  <div>{(a.proof.categories || []).join(", ")}</div>
                  <div style={{ color: T_NEW.ink56 }}>{t("admin.apps.field.contact")}</div>
                  <div>{a.proof.contactHandle}</div>
                  <div style={{ color: T_NEW.ink56, fontWeight: 600 }}>{t("admin.apps.field.verificationPost")}</div>
                  <a href={a.proof.verificationPostUrl} target="_blank" rel="noopener noreferrer"
                     style={{ color: T_NEW.accent, wordBreak: "break-all", fontWeight: 600 }}>
                    {a.proof.verificationPostUrl}
                  </a>
                </div>
              )}
              {a.note && a.status === "rejected" && (
                <div style={{ marginTop: 10, padding: 10,
                              background: `${T_NEW.danger}10`, borderRadius: 6,
                              fontSize: 13, color: T_NEW.ink }}>
                  <strong>{t("admin.apps.rejectReason")}</strong> {a.note}
                </div>
              )}
              {a.status === "submitted" && (
                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  <Btn variant="primary"
                       onClick={async () => {
                         try {
                           await api.adminDecideApplication(a.uid, "approve");
                           toast.success(t("admin.apps.approved.ok"));
                           await loadApps();
                         } catch (e) { toast.error(e.message); }
                       }}>
                    {t("admin.apps.approve")}
                  </Btn>
                  {appRejectFor === a.uid ? (
                    <>
                      <Inp value={appRejectNote} onChange={setAppRejectNote}
                           placeholder={t("admin.apps.reject.placeholder")} />
                      <Btn variant="ghost"
                           onClick={async () => {
                             if (!appRejectNote.trim()) {
                               toast.error(t("admin.apps.reject.required"));
                               return;
                             }
                             try {
                               await api.adminDecideApplication(a.uid, "reject", appRejectNote.trim());
                               toast.success(t("admin.apps.reject.ok"));
                               setAppRejectFor(null); setAppRejectNote("");
                               await loadApps();
                             } catch (e) { toast.error(e.message); }
                           }}>
                        {t("admin.apps.reject.confirm")}
                      </Btn>
                      <Btn variant="ghost"
                           onClick={() => { setAppRejectFor(null); setAppRejectNote(""); }}>
                        {t("admin.apps.reject.cancel")}
                      </Btn>
                    </>
                  ) : (
                    <Btn variant="ghost"
                         onClick={() => { setAppRejectFor(a.uid); setAppRejectNote(""); }}>
                      {t("admin.apps.reject")}
                    </Btn>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {tab === "curated" && (
        <AdminCuratedPanel user={user} />
      )}
      {tab === "subscriptions" && (
        <div>
          <div style={{ marginBottom: 14, fontSize: 13, color: T.textSec, lineHeight: 1.5 }}>
            {t("admin.subs.hint")}
          </div>
          {allSubs.length === 0 && (
            <p style={{ color: T.textSec, fontSize: 13, padding: 12 }}>
              {t("admin.subs.empty")}
            </p>
          )}
          {allSubs.map((o) => {
            const isPending = o.status === "pending";
            const statusColor =
              o.status === "pending"   ? T.accent
              : o.status === "approved" ? "#3a7d3a"
              : o.status === "rejected" ? T.danger
              :                            T.textSec;
            return (
              <div key={o.id}
                   style={{ padding: 12, background: T.sidebar,
                            border: `1px solid ${T.border}`,
                            borderRadius: 8, marginBottom: 8 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start",
                              flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontWeight: 600, fontSize: 14,
                                  display: "flex", alignItems: "center", gap: 8,
                                  flexWrap: "wrap" }}>
                      {o.user?.displayName || o.userId.slice(0, 8)}
                      <span style={{ color: T.textSec, fontSize: 12 }}>
                        {o.user?.email}
                      </span>
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, color: T.text }}>
                      <strong>{o.tier.toUpperCase()}</strong>
                      {" · "}
                      {o.billingPeriod === "annual" ? t("admin.subs.period.annual") : t("admin.subs.period.monthly")}
                      {" · "}
                      <span style={{ color: T.textSec }}>
                        ${(o.amountCents / 100).toFixed(0)} {o.currency}
                      </span>
                      {" · "}
                      <span style={{ color: statusColor, textTransform: "uppercase",
                                     fontWeight: 700, fontSize: 11 }}>
                        {o.status}
                      </span>
                    </div>
                    {o.notes && (
                      <div style={{ marginTop: 6, fontSize: 12, color: T.text,
                                    fontStyle: "italic" }}>
                        “{o.notes}”
                      </div>
                    )}
                    {o.adminNotes && (
                      <div style={{ marginTop: 6, fontSize: 12, color: T.textSec }}>
                        {t("admin.subs.adminPrefix")} {o.adminNotes}
                      </div>
                    )}
                    <div style={{ marginTop: 4, fontSize: 11, color: T.textSec }}>
                      {t("admin.subs.filed", { when: new Date(o.createdAt * 1000).toLocaleString("ru-RU") })}
                      {o.decidedAt && t("admin.subs.decided", { when: new Date(o.decidedAt * 1000).toLocaleString("ru-RU") })}
                    </div>
                  </div>
                  {isPending && (
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button
                        onClick={async () => {
                          const note = await toast.prompt(
                            t("admin.subs.approve.confirm", { tier: o.tier, period: o.billingPeriod, user: o.user?.displayName || o.userId }),
                            { placeholder: t("admin.subs.approve.placeholder") },
                          );
                          if (note === null) return;
                          try {
                            await api.approveSubscriptionOrder(o.id, note || "");
                            await loadSubs();
                            toast.success(t("admin.subs.approve.ok"));
                          } catch (e) { toast.error(e.message); }
                        }}
                        style={{ background: "#3a7d3a20", color: "#3a7d3a",
                                 border: "none", cursor: "pointer", padding: "6px 12px",
                                 borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                        {t("admin.subs.approve")}
                      </button>
                      <button
                        onClick={async () => {
                          const note = await toast.prompt(
                            t("admin.subs.reject.confirm"),
                            { placeholder: t("admin.subs.reject.placeholder") },
                          );
                          if (note === null) return;
                          try {
                            await api.rejectSubscriptionOrder(o.id, note || "");
                            await loadSubs();
                            toast.success(t("admin.subs.reject.ok"));
                          } catch (e) { toast.error(e.message); }
                        }}
                        style={{ background: `${T.danger}20`, color: T.danger,
                                 border: "none", cursor: "pointer", padding: "6px 12px",
                                 borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                        {t("admin.subs.reject")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {tab === "shipments" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
            <select value={shipmentsFilter.status}
                    onChange={(e) => setShipmentsFilter(f => ({ ...f, status: e.target.value }))}
                    style={{ flex: 1, minWidth: 160, background: T.bg, color: T.text, border: `1px solid ${T.border}`, padding: "8px 10px", borderRadius: 8, fontSize: 13 }}>
              <option value="">{t("admin.ships.filter.all")}</option>
              {SHIPMENT_STATUSES.map(s => <option key={s} value={s}>{t(`admin.ships.status.${s}`)}</option>)}
            </select>
            <button onClick={loadShipments} disabled={shipmentsLoading}
                    style={{ background: `${T.accent}20`, color: T.accent, border: "none", cursor: "pointer", padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
              {shipmentsLoading ? t("admin.ships.refreshing") : t("admin.ships.refresh")}
            </button>
          </div>

          {shipments.length === 0 && !shipmentsLoading ? (
            <div style={{ color: T.textSec, fontSize: 13, padding: 24, textAlign: "center", background: T.bg, borderRadius: 8 }}>
              {t("admin.ships.empty")}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {shipments.map((s) => (
                <AdminShipmentCard key={s.id} s={s} onChanged={loadShipments} />
              ))}
            </div>
          )}
        </div>
      )}
      {tab === "settings" && (
        <div>
          <div style={{ fontSize: 13, color: T.textSec, marginBottom: 12, lineHeight: 1.5 }}>
            {t("admin.fx.hint")}
          </div>
          <div style={{ background: T.bg, padding: 16, borderRadius: 10 }}>
            <div style={{ fontSize: 12, color: T.textSec, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 14 }}>
              {t("admin.fx.title")}
            </div>
            {/* Two-up grid that drops to single-column on narrow widths
                via flex-wrap. min-width:120 keeps inputs usable when
                wrapped. */}
            <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 140px", minWidth: 0 }}>
                <div style={{ fontSize: 11, color: T.textSec, marginBottom: 4 }}>{t("admin.fx.base")}</div>
                <input
                  type="number" step="0.01" min="0"
                  value={fxBaseInput}
                  onChange={(e) => setFxBaseInput(e.target.value)}
                  style={{ width: "100%", background: T.sidebar, color: T.text, border: `1px solid ${T.border}`,
                           padding: "10px 12px", borderRadius: 10, fontSize: 16, fontWeight: 600 }}
                />
              </div>
              <div style={{ flex: "1 1 100px", minWidth: 0 }}>
                <div style={{ fontSize: 11, color: T.textSec, marginBottom: 4 }}>{t("admin.fx.markup")}</div>
                <input
                  type="number" step="0.5" min="0"
                  value={fxMarkupInput}
                  onChange={(e) => setFxMarkupInput(e.target.value)}
                  style={{ width: "100%", background: T.sidebar, color: T.text, border: `1px solid ${T.border}`,
                           padding: "10px 12px", borderRadius: 10, fontSize: 16, fontWeight: 600 }}
                />
              </div>
            </div>
            <div style={{ background: T.sidebar, padding: "10px 14px", borderRadius: 8, marginBottom: 14,
                          display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontSize: 12, color: T.textSec, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
                {t("admin.fx.effective")}
              </span>
              <span style={{ fontSize: 20, fontWeight: 700, color: T.accent }}>
                {previewEffective != null ? `1$ = ${previewEffective.toFixed(2)}₽` : "—"}
              </span>
            </div>
            {fx && (
              <div style={{ fontSize: 11, color: T.textSec, marginBottom: 10 }}>
                {t("admin.fx.currentSaved", { base: fx.base, markup: fx.markupPct })}<strong>{fx.effective.toFixed(2)}</strong>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn disabled={fxSaving} onClick={saveFx}>{fxSaving ? t("admin.fx.saving") : t("admin.fx.save")}</Btn>
            </div>
          </div>

          {/* ─── FX auto-updater (CBR hourly poll) ───
              Lives in its own card so the operator-controls card above
              stays clean. State block shows last fetch outcome + the
              raw CBR rate (pre-markup); buttons let the operator force
              a fetch or pause the loop. Auto-update changes only the
              base — markup stays where the operator set it. */}
          {fx?.auto && (() => {
            const a = fx.auto;
            const lastWhen = a.lastRunAt ? new Date(a.lastRunAt) : null;
            const lastChanged = a.lastChangedAt ? new Date(a.lastChangedAt) : null;
            const rel = (d) => {
              if (!d) return t("admin.fx.auto.never");
              const s = Math.round((Date.now() - d.getTime()) / 1000);
              if (s < 60) return t("admin.fx.auto.relSec", { n: s });
              if (s < 3600) return t("admin.fx.auto.relMin", { n: Math.round(s / 60) });
              if (s < 86400) return t("admin.fx.auto.relHour", { n: Math.round(s / 3600) });
              return t("admin.fx.auto.relDay", { n: Math.round(s / 86400) });
            };
            const ok = a.lastStatus === "ok";
            const fail = a.lastStatus === "fail";
            const dotColor = !a.enabled ? T.textSec : fail ? T.danger : ok ? T.online : T.accent;
            return (
              <div style={{ background: T.bg, padding: 16, borderRadius: 10, marginTop: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                              marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: dotColor,
                                   boxShadow: a.enabled && !fail ? `0 0 0 3px ${dotColor}30` : "none" }} />
                    <span style={{ fontSize: 12, color: T.textSec, fontWeight: 700,
                                   textTransform: "uppercase", letterSpacing: 0.6 }}>
                      {t("admin.fx.auto.title")}
                    </span>
                  </div>
                  {/* Toggle. Click = flip enabled, optimistic UI handled by toggleFxAuto. */}
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                                  fontSize: 12, color: T.textSec }}>
                    <input type="checkbox" checked={!!a.enabled}
                           onChange={(e) => toggleFxAuto(e.target.checked)}
                           style={{ accentColor: T.accent, width: 16, height: 16 }} />
                    {a.enabled ? t("admin.fx.auto.on") : t("admin.fx.auto.off")}
                  </label>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr",
                              gap: "6px 14px", fontSize: 12, color: T.text, marginBottom: 12 }}>
                  <span style={{ color: T.textSec }}>{t("admin.fx.auto.lastFetch")}</span>
                  <span>
                    {lastWhen ? lastWhen.toLocaleString() : "—"}
                    {lastWhen && <span style={{ color: T.textSec }}> · {rel(lastWhen)}</span>}
                  </span>
                  <span style={{ color: T.textSec }}>{t("admin.fx.auto.status")}</span>
                  <span style={{ color: fail ? T.danger : ok ? T.online : T.textSec, fontWeight: 600 }}>
                    {ok ? t("admin.fx.auto.status.ok")
                      : fail ? t("admin.fx.auto.status.fail", { err: a.lastError ?? t("admin.fx.auto.status.unknown") })
                      : "—"}
                  </span>
                  <span style={{ color: T.textSec }}>{t("admin.fx.auto.cbrRate")}</span>
                  <span style={{ fontFamily: "monospace" }}>
                    {a.lastSourceRate != null ? t("admin.fx.auto.cbrRate.value", { rate: a.lastSourceRate.toFixed(2) }) : "—"}
                  </span>
                  <span style={{ color: T.textSec }}>{t("admin.fx.auto.lastChange")}</span>
                  <span>
                    {lastChanged ? `${lastChanged.toLocaleString()} · ${rel(lastChanged)}` : t("admin.fx.auto.noChange")}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                              gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, color: T.textSec, fontStyle: "italic" }}>
                    {t("admin.fx.auto.source")}
                  </span>
                  <Btn variant="ghost" disabled={fxRefreshing} onClick={refreshFx}>
                    {fxRefreshing ? t("admin.fx.auto.fetching") : t("admin.fx.auto.refreshNow")}
                  </Btn>
                </div>
              </div>
            );
          })()}

          {/* ─── Payment methods (Feature 1) ─── */}
          <div style={{ background: T.bg, padding: 16, borderRadius: 10, marginTop: 16 }}>
            <div style={{ fontSize: 12, color: T.textSec, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
              {t("admin.pm.title")}
            </div>
            <div style={{ fontSize: 12, color: T.textSec, marginBottom: 14, lineHeight: 1.5 }}>
              {t("admin.pm.hint")}
            </div>

            {paymentMethods === null ? (
              <div style={{ color: T.textSec, fontSize: 13 }}>{t("admin.pm.loading")}</div>
            ) : (
              <>
                {/* Stablecoins are multi-network. Operator can hold
                    liquidity on several chains (TRC20, ERC20, Solana,
                    Polygon, etc.) and add one row per chain per coin.
                    Network labels are free-text so we're not constrained
                    by an enum we'd need to keep in sync. The legacy
                    single-TRC20 USDT field is auto-promoted on the
                    server, so it shows up as a "TRC20" row here without
                    any operator action. */}
                <StablecoinRowEditor
                  title={t("admin.pm.usdt.title")}
                  rows={paymentMethods.usdt ?? []}
                  onChange={(rows) => updatePmField("usdt", rows)} />
                <StablecoinRowEditor
                  title={t("admin.pm.usdc.title")}
                  rows={paymentMethods.usdc ?? []}
                  onChange={(rows) => updatePmField("usdc", rows)} />
                <PmGroup title={t("admin.pm.sbp.title")}>
                  <PmField label={t("admin.pm.sbp.phone")} placeholder="+7…"
                           value={paymentMethods.sbp_phone}
                           onChange={(v) => updatePmField("sbp_phone", v)} />
                  <PmField label={t("admin.pm.sbp.name")} placeholder="Иван И."
                           value={paymentMethods.sbp_name}
                           onChange={(v) => updatePmField("sbp_name", v)} />
                </PmGroup>
                <PmGroup title={t("admin.pm.card.title")}>
                  <PmField label={t("admin.pm.card.number")} placeholder="2200 …"
                           value={paymentMethods.card_number}
                           onChange={(v) => updatePmField("card_number", v)} />
                  <PmField label={t("admin.pm.card.bank")} placeholder="Тинькофф"
                           value={paymentMethods.card_bank}
                           onChange={(v) => updatePmField("card_bank", v)} />
                  <PmField label={t("admin.pm.card.holder")} placeholder="IVAN IVANOV"
                           value={paymentMethods.card_holder}
                           onChange={(v) => updatePmField("card_holder", v)} />
                </PmGroup>
                <PmGroup title={t("admin.pm.instructions.title")}>
                  <PmArea label={t("admin.pm.instructions.ru")} placeholder={t("admin.pm.instructions.placeholderRu")}
                          value={paymentMethods.instructions_ru}
                          onChange={(v) => updatePmField("instructions_ru", v)} />
                  <PmArea label={t("admin.pm.instructions.en")} placeholder={t("admin.pm.instructions.placeholderEn")}
                          value={paymentMethods.instructions_en}
                          onChange={(v) => updatePmField("instructions_en", v)} />
                </PmGroup>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                  <Btn disabled={pmSaving} onClick={savePaymentMethods}>
                    {pmSaving ? t("admin.pm.saving") : t("admin.pm.save")}
                  </Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {tab === "groups" && (
        <div>
          <Btn onClick={onCreateGroup} full style={{ marginBottom: 16 }}>{t("admin.groups.create")}</Btn>
          {groups.map(g => (
            <GroupRow key={g.id} g={g} t={t} onRefresh={onRefresh}
                      onAddTopic={onAddTopic} toast={toast} />
          ))}
        </div>
      )}
      {tab === "users" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {users.map(u => (
            <div key={u.uid} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: T.bg, borderRadius: 10, opacity: u.bannedAt ? 0.5 : 1 }}>
              <Av name={u.displayName} size={38} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  {u.displayName}
                  {(u.role === "admin" || u.isAdmin) && <span style={{ fontSize: 10, color: T.accent, background: `${T.accent}20`, padding: "1px 6px", borderRadius: 4, fontWeight: 700 }}>{t("admin.users.role.admin")}</span>}
                  {u.role === "manager" && <span style={{ fontSize: 10, color: T.accent, background: `${T.accent}20`, padding: "1px 6px", borderRadius: 4, fontWeight: 700 }}>{t("admin.users.role.manager")}</span>}
                  {u.isPremium && (
                    <span title={u.premiumUntil
                            ? t("admin.users.premium.until", { date: new Date(u.premiumUntil * 1000).toLocaleDateString("ru-RU") })
                            : t("admin.users.role.premium")}
                          style={{ fontSize: 10, color: "#a04420",
                                   background: "#f9c8a720",
                                   border: "1px solid #a0442040",
                                   padding: "1px 6px", borderRadius: 4,
                                   fontWeight: 700 }}>
                      {t("admin.users.role.premium")}
                    </span>
                  )}
                  {u.bannedAt && <span style={{ fontSize: 10, color: T.danger, background: `${T.danger}20`, padding: "1px 6px", borderRadius: 4, fontWeight: 700 }}>{t("admin.users.role.banned")}</span>}
                  {u.uid === user.uid && <span style={{ fontSize: 10, color: T.textSec }}>{t("admin.users.you")}</span>}
                </div>
                <div style={{ fontSize: 12, color: T.textSec }}>{u.email}</div>
                {u.banReason && <div style={{ fontSize: 11, color: T.danger, marginTop: 2 }}>{t("admin.users.banReason")} {u.banReason}</div>}
              </div>
              {u.uid !== user.uid && (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <select
                    value={u.role || (u.isAdmin ? "admin" : "customer")}
                    onChange={async (e) => { await api.setRole(u.uid, e.target.value); onRefresh(); }}
                    style={{ background: T.bg, color: T.text, border: `1px solid ${T.accent}40`, padding: "4px 8px", borderRadius: 6, fontSize: 11, cursor: "pointer" }}
                  >
                    <option value="customer">{t("admin.users.role.customer.opt")}</option>
                    <option value="manager">{t("admin.users.role.manager.opt")}</option>
                    <option value="admin">{t("admin.users.role.admin.opt")}</option>
                  </select>
                  {/* Premium grant control — customer-role only (admins
                      and managers don't need premium since they don't
                      forward as resellers). Dropdown bumps premium_until
                      by N months from max(now, current); "Revoke"
                      pins it to now. Selecting a duration when the user
                      is already premium STACKS on the existing expiry,
                      matching the API's renewal semantics. */}
                  {u.role === "customer" && (
                    <select
                      value=""
                      onChange={async (e) => {
                        const v = e.target.value;
                        if (!v) return;
                        const months = v === "revoke" ? 0 : parseInt(v, 10);
                        const label = v === "revoke"
                          ? t("admin.users.premium.revoke.confirm", { name: u.displayName })
                          : t("admin.users.premium.grant", { months, name: u.displayName });
                        if (!await toast.confirm(label)) {
                          e.target.value = "";
                          return;
                        }
                        try {
                          await api.adminSetPremium(u.uid, months);
                          onRefresh();
                        } catch (err) {
                          toast.error(err.message);
                        }
                        e.target.value = "";
                      }}
                      style={{ background: T.bg, color: T.text,
                               border: `1px solid #a0442040`,
                               padding: "4px 8px", borderRadius: 6,
                               fontSize: 11, cursor: "pointer" }}
                      title={u.isPremium && u.premiumUntil
                              ? t("admin.users.premium.until", { date: new Date(u.premiumUntil * 1000).toLocaleDateString("ru-RU") })
                              : t("admin.users.premium.free")}
                    >
                      <option value="">{t("admin.users.premium.label")}</option>
                      <option value="1">{t("admin.users.premium.1mo")}</option>
                      <option value="3">{t("admin.users.premium.3mo")}</option>
                      <option value="12">{t("admin.users.premium.12mo")}</option>
                      <option value="revoke">{t("admin.users.premium.revoke")}</option>
                    </select>
                  )}
                  {u.role !== "admin" && (
                    u.bannedAt ? (
                      <button onClick={async () => {
                        try { await api.unbanUser(u.uid); onRefresh(); } catch (e) { toast.error(e.message); }
                      }} style={{ background: `${T.accent}20`, color: T.accent, border: "none", cursor: "pointer", padding: "4px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{t("admin.users.unban")}</button>
                    ) : (
                      <button onClick={async () => {
                        const reason = await toast.prompt(t("admin.users.ban.prompt", { name: u.displayName }), { placeholder: t("admin.users.ban.placeholder") });
                        if (reason === null) return;
                        try { await api.banUser(u.uid, reason || null); onRefresh(); } catch (e) { toast.error(e.message); }
                      }} style={{ background: `${T.danger}20`, color: T.danger, border: "none", cursor: "pointer", padding: "4px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{t("admin.users.ban")}</button>
                    )
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {tab === "channels" && (
        <div>
          <div style={{ fontSize: 13, color: T.textSec, marginBottom: 12, lineHeight: 1.5 }}>
            {t("admin.channels.hint")}
          </div>
          <StoreFeePanel channels={adminChannels} onSaved={loadAdminChannels} />
          {adminChannels.length === 0 ? (
            <div style={{ color: T.textSec, fontSize: 13, padding: 24, textAlign: "center", background: T.bg, borderRadius: 8 }}>
              {t("admin.channels.empty")}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {adminChannels.map((ch) => (
                <AdminChannelRow
                  key={ch.id}
                  channel={ch}
                  users={users}
                  onSaved={loadAdminChannels}
                  onDeleted={loadAdminChannels}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {tab === "wa" && (
        <div>
          <div style={{ fontSize: 13, color: T.textSec, marginBottom: 12, lineHeight: 1.5 }}>
            {t("admin.wa.hint")}
          </div>

          <div style={{ background: T.bg, padding: 12, borderRadius: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: T.textSec, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
              {t("admin.wa.configured")}
            </div>
            {waRoutes.length === 0 ? (
              <div style={{ fontSize: 12, color: T.textSec, padding: 8 }}>
                {t("admin.wa.configured.empty")}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {waRoutes.map((r) => (
                  <WaRouteRow key={r.id} route={r} channels={adminChannels} onChanged={loadWaData} />
                ))}
              </div>
            )}
          </div>

          <div style={{ background: T.bg, padding: 12, borderRadius: 10 }}>
            <div style={{ fontSize: 11, color: T.textSec, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
              {t("admin.wa.recent")}
            </div>
            {waRecent.length === 0 ? (
              <div style={{ fontSize: 12, color: T.textSec, padding: 8 }}>
                {t("admin.wa.recent.empty")}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {waRecent.map((s) => (
                  <div key={s.senderPhone}
                       style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
                                background: T.sidebar, borderRadius: 6, fontSize: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontFamily: "monospace" }}>{s.senderPhone}</div>
                      <div style={{ color: T.textSec, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t("admin.wa.msgsLast", { n: s.msgCount, text: (s.latestText ?? "").slice(0, 60) })}
                      </div>
                    </div>
                    {s.hasRoute ? (
                      <span style={{ fontSize: 10, color: T.online, fontWeight: 600 }}>{t("admin.wa.routed")}</span>
                    ) : (
                      <button onClick={async () => {
                        const name = await toast.prompt(t("admin.wa.addRoute.prompt", { phone: s.senderPhone }), { placeholder: t("admin.wa.addRoute.placeholder") });
                        if (name === null) return;
                        try {
                          await api.upsertWaRoute({ senderPhone: s.senderPhone, displayName: name || null, channelId: null, autoPublish: false });
                          await loadWaData();
                          toast.success(t("admin.wa.addRoute.ok"));
                        } catch (e) { toast.error(e.message); }
                      }} style={{ background: T.accent, color: "#fff", border: "none", cursor: "pointer", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                        {t("admin.wa.addRoute")}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {tab === "tokens" && (
        <div>
          {newTokenPlain && (
            <div style={{ background: `${T.accent}15`, border: `1px solid ${T.accent}`, padding: 14, borderRadius: 10, marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: T.accent, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
                {t("admin.tokens.newToken")}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input readOnly value={newTokenPlain} style={{ flex: 1, background: T.bg, color: T.text, border: `1px solid ${T.border}`, padding: "8px 10px", borderRadius: 8, fontFamily: "monospace", fontSize: 12 }} />
                <Btn onClick={() => { navigator.clipboard.writeText(newTokenPlain); }}>{t("admin.tokens.copy")}</Btn>
                <Btn variant="ghost" onClick={() => setNewTokenPlain(null)}>{t("admin.tokens.dismiss")}</Btn>
              </div>
            </div>
          )}

          <div style={{ background: T.bg, padding: 14, borderRadius: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: T.textSec, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>{t("admin.tokens.create")}</div>
            <Inp label={t("admin.tokens.name")} value={tokenName} onChange={setTokenName} placeholder={t("admin.tokens.name.placeholder")} />
            <Inp label={t("admin.tokens.scopes")} value={tokenScopes} onChange={setTokenScopes} placeholder={t("admin.tokens.scopes.placeholder")} />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn disabled={!tokenName.trim()} onClick={createToken}>{t("admin.tokens.generate")}</Btn>
            </div>
          </div>

          <div style={{ fontSize: 12, color: T.textSec, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>{t("admin.tokens.active")}</div>
          {tokens.filter(tk => !tk.revokedAt).length === 0 ? (
            <div style={{ color: T.textSec, fontSize: 13, padding: 14, textAlign: "center", background: T.bg, borderRadius: 8 }}>{t("admin.tokens.active.empty")}</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {tokens.filter(tk => !tk.revokedAt).map(tk => (
                <div key={tk.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: T.bg, borderRadius: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{tk.name}</div>
                    <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>
                      {tk.scopes.join(", ")} · {t("admin.tokens.createdAt", { date: new Date(tk.createdAt * 1000).toLocaleDateString() })}
                      {tk.lastUsedAt && t("admin.tokens.lastUsedAt", { date: new Date(tk.lastUsedAt * 1000).toLocaleString() })}
                    </div>
                  </div>
                  <button onClick={async () => {
                    if (!await toast.confirm(t("admin.tokens.revoke.confirm", { name: tk.name }))) return;
                    try { await api.revokeServiceToken(tk.id); await loadTokens(); } catch (e) { toast.error(e.message); }
                  }} style={{ background: `${T.danger}20`, color: T.danger, border: "none", cursor: "pointer", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{t("admin.tokens.revoke")}</button>
                </div>
              ))}
            </div>
          )}

          {tokens.some(tk => tk.revokedAt) && (
            <details style={{ marginTop: 14 }}>
              <summary style={{ fontSize: 12, color: T.textSec, cursor: "pointer" }}>{t("admin.tokens.revoked.section", { n: tokens.filter(tk => tk.revokedAt).length })}</summary>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
                {tokens.filter(tk => tk.revokedAt).map(tk => (
                  <div key={tk.id} style={{ padding: "6px 10px", fontSize: 12, color: T.textSec }}>
                    {t("admin.tokens.revoked.row", { name: tk.name, date: new Date(tk.revokedAt * 1000).toLocaleDateString() })}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
      {tab === "redemptions" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {pendingReds.length === 0 ? (
            <div style={{ color: T.textSec, fontSize: 13, padding: 20, textAlign: "center" }}>{t("admin.reds.empty")}</div>
          ) : pendingReds.map(r => (
            <div key={r.id} style={{ background: T.bg, padding: 12, borderRadius: 10, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{r.user.displayName} <span style={{ color: T.textSec, fontWeight: 400 }}>· {r.user.email}</span></div>
                  <div style={{ fontSize: 12, color: T.textSec, marginTop: 2 }}>
                    {fmt(r.amountCents)} · {r.method.replaceAll("_", " ")} · {t("admin.reds.requested", { when: new Date(r.requestedAt * 1000).toLocaleString() })}
                  </div>
                  {r.deliveryDetails?.email && <div style={{ fontSize: 12, color: T.accent, marginTop: 2 }}>{t("admin.reds.sendTo")} {r.deliveryDetails.email}</div>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={async () => {
                    if (!await toast.confirm(t("admin.reds.fulfill.confirm", { amount: fmt(r.amountCents), email: r.user.email }))) return;
                    const note = await toast.prompt(
                      t("admin.reds.fulfill.notePrompt"),
                      { placeholder: t("admin.reds.fulfill.placeholder"), multiline: true },
                    );
                    if (note === null) return;
                    try {
                      await api.fulfillRedemption(r.id, note ? { note } : undefined);
                      await loadReds();
                    } catch (e) { toast.error(e.message); }
                  }} style={{ background: T.accent, color: "#fff", border: "none", cursor: "pointer", padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{t("admin.reds.fulfill")}</button>
                  <button onClick={async () => {
                    const reason = await toast.prompt(t("admin.reds.cancel.title"), { placeholder: t("admin.reds.cancel.placeholder") });
                    if (reason === null || !reason.trim()) return;
                    try {
                      await api.cancelRedemption(r.id, reason);
                      await loadReds();
                    } catch (e) { toast.error(e.message); }
                  }} style={{ background: `${T.danger}20`, color: T.danger, border: "none", cursor: "pointer", padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{t("admin.reds.cancel")}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {tab === "templates" && (
        <div>
          <div style={{ fontSize: 13, color: T.textSec, marginBottom: 12, lineHeight: 1.5 }}>
            {t("admin.templates.hint.prefix")}<code>{`{customer_name}`}</code>
            <code>{` {order_id}`}</code>{t("admin.templates.hint.suffix")}
          </div>
          <Btn full onClick={createTemplate} style={{ marginBottom: 14 }}>{t("admin.templates.new")}</Btn>
          {templates.length === 0 ? (
            <div style={{ color: T.textSec, fontSize: 13, padding: 24, textAlign: "center", background: T.bg, borderRadius: 8 }}>
              {t("admin.templates.empty")}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {templates.map((tpl) => (
                <div key={tpl.id} style={{ background: T.bg, padding: 12, borderRadius: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{tpl.name}</div>
                    <button onClick={() => deleteTemplate(tpl.id, tpl.name)}
                            style={{ background: `${T.danger}20`, color: T.danger, border: "none", cursor: "pointer", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                      {t("admin.templates.delete")}
                    </button>
                  </div>
                  {tpl.bodyRu && (
                    <div style={{ fontSize: 12, color: T.text, whiteSpace: "pre-wrap", padding: "6px 0" }}>
                      <span style={{ fontSize: 10, color: T.textSec, fontWeight: 600, marginRight: 6 }}>RU</span>
                      {tpl.bodyRu}
                    </div>
                  )}
                  {tpl.bodyEn && (
                    <div style={{ fontSize: 12, color: T.textSec, whiteSpace: "pre-wrap", padding: "6px 0", borderTop: `1px solid ${T.border}`, marginTop: 4 }}>
                      <span style={{ fontSize: 10, color: T.textSec, fontWeight: 600, marginRight: 6 }}>EN</span>
                      {tpl.bodyEn}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {tab === "audit" && (
        <div>
          {/* Filter bar — narrow by action or target_type. Both feed the
              loadAudit() effect via auditFilter state. */}
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <select value={auditFilter.action}
                    onChange={(e) => setAuditFilter(f => ({ ...f, action: e.target.value }))}
                    style={{ flex: 1, minWidth: 160, background: T.bg, color: T.text, border: `1px solid ${T.border}`, padding: "8px 10px", borderRadius: 8, fontSize: 13 }}>
              <option value="">{t("admin.audit.action.all")}</option>
              <option value="user.banned">user.banned</option>
              <option value="user.unbanned">user.unbanned</option>
              <option value="user.role_changed">user.role_changed</option>
              <option value="channel.created">channel.created</option>
              <option value="channel.updated">channel.updated</option>
              <option value="channel.owner_changed">channel.owner_changed</option>
              <option value="channel.deleted">channel.deleted</option>
              <option value="post.published">post.published</option>
              <option value="post.rejected">post.rejected</option>
              <option value="post.archived">post.archived</option>
              <option value="post.deleted">post.deleted</option>
              <option value="redemption.fulfilled">redemption.fulfilled</option>
              <option value="redemption.cancelled">redemption.cancelled</option>
              <option value="service_token.created">service_token.created</option>
              <option value="service_token.revoked">service_token.revoked</option>
              <option value="message.edited">message.edited</option>
              <option value="message.deleted">message.deleted</option>
            </select>
            <select value={auditFilter.targetType}
                    onChange={(e) => setAuditFilter(f => ({ ...f, targetType: e.target.value }))}
                    style={{ flex: 1, minWidth: 140, background: T.bg, color: T.text, border: `1px solid ${T.border}`, padding: "8px 10px", borderRadius: 8, fontSize: 13 }}>
              <option value="">{t("admin.audit.target.all")}</option>
              <option value="user">user</option>
              <option value="channel">channel</option>
              <option value="post">post</option>
              <option value="redemption">redemption</option>
              <option value="service_token">service_token</option>
              <option value="message">message</option>
            </select>
            <button onClick={loadAudit}
                    disabled={auditLoading}
                    style={{ background: `${T.accent}20`, color: T.accent, border: "none", cursor: "pointer", padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
              {auditLoading ? t("admin.audit.reloading") : t("admin.audit.reload")}
            </button>
          </div>

          {auditEntries.length === 0 && !auditLoading ? (
            <div style={{ color: T.textSec, fontSize: 13, padding: 24, textAlign: "center", background: T.bg, borderRadius: 8 }}>
              {t("admin.audit.empty")}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {auditEntries.map((entry) => {
                const ts = new Date(entry.ts * 1000);
                const detailsText = Object.keys(entry.details ?? {}).length
                  ? JSON.stringify(entry.details)
                  : "";
                return (
                  <div key={entry.id} style={{ background: T.bg, padding: "8px 12px", borderRadius: 8, fontSize: 12, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ color: T.textSec, fontSize: 11 }}>{ts.toLocaleString()}</span>
                      <span style={{ color: T.accent, fontWeight: 700 }}>{entry.action}</span>
                      {entry.targetType && <span style={{ color: T.text }}>{entry.targetType}:{entry.targetId?.slice(0, 8) ?? "?"}</span>}
                      <span style={{ marginLeft: "auto", color: T.textSec, fontSize: 11 }}>{entry.actorEmail || t("admin.audit.system")}</span>
                    </div>
                    {detailsText && (
                      <div style={{ color: T.textSec, marginTop: 4, fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                        {detailsText}
                      </div>
                    )}
                    {entry.requestId && (
                      <div style={{ color: T.textSec, marginTop: 2, fontSize: 10, opacity: 0.6 }}>
                        {t("admin.audit.req", { id: entry.requestId })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Admin shipment card ──────────────────────────────────────────────
//
// Per-shipment row in the admin Shipments tab. Inline tracking-fill
// form for 'ready' shipments, then a Mark shipped button (which
// cascades all constituent orders to shipped via the backend TX). For
// shipped shipments, a Mark delivered button.
function AdminShipmentCard({ s, onChanged }) {
  const toast = useToast();
  const t = useT();
  const [carrier, setCarrier] = useState(s.carrier ?? "");
  const [trackingNumber, setTrackingNumber] = useState(s.trackingNumber ?? "");
  const [trackingUrl, setTrackingUrl] = useState(s.trackingUrl ?? "");
  const [weightKg, setWeightKg] = useState(s.weightKg != null ? String(s.weightKg) : "");
  const [saving, setSaving] = useState(false);

  const statusColor = s.status === "delivered" ? T.online
                    : s.status === "cancelled" ? T.danger
                    : s.status === "ready"     ? T.accent
                    :                            T.textSec;

  const saveTracking = async () => {
    setSaving(true);
    try {
      const patch = { carrier, trackingNumber, trackingUrl };
      const w = parseFloat(weightKg);
      if (isFinite(w) && w > 0) patch.weightKg = w;
      await api.updateAdminShipment(s.id, patch);
      toast.success(t("admin.ships.tracking.saved"));
      onChanged();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const markShipped = async () => {
    if (!trackingNumber.trim()) { toast.error(t("admin.ships.tracking.required")); return; }
    if (!await toast.confirm(t("admin.ships.markShipped.confirm", { id: s.id.slice(0,8), n: s.orders.length }))) return;
    try { await api.markShipmentShipped(s.id); onChanged(); toast.success(t("admin.ships.markShipped.ok")); }
    catch (e) { toast.error(e.message); }
  };
  const markDelivered = async () => {
    if (!await toast.confirm(t("admin.ships.markDelivered.confirm", { id: s.id.slice(0,8) }))) return;
    try { await api.markShipmentDelivered(s.id); onChanged(); toast.success(t("admin.ships.markDelivered.ok")); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div style={{ background: T.bg, padding: 12, borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>#{s.id.slice(0, 8)}</div>
          <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>
            {s.customer?.displayName} · {s.customer?.email}
          </div>
        </div>
        <span style={{ fontSize: 11, color: statusColor, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
          {s.status}
        </span>
      </div>

      {s.recipientName && (
        <div style={{ fontSize: 11, color: T.accent, marginBottom: 8, whiteSpace: "pre-wrap" }}>
          → {s.recipientName}{s.recipientPhone ? ` · ${s.recipientPhone}` : ""}
          {s.shipAddress ? `\n${s.shipAddress}` : ""}
          {s.shipCountry ? `, ${s.shipCountry}` : ""}
        </div>
      )}

      {/* Customs declared-value summary (Feature 3). Red row when over
          the 200 EUR threshold so the operator sees it before printing. */}
      {s.totalDeclaredUsdCents != null && (
        <div style={{ marginBottom: 8, padding: "6px 10px",
                      background: s.overCustomsThreshold ? `${T.danger}20` : `${T.accent}10`,
                      borderRadius: 6, fontSize: 11, display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: s.overCustomsThreshold ? T.danger : T.text, fontWeight: 600 }}>
            {s.overCustomsThreshold ? t("admin.ships.customs.over") : t("admin.ships.customs.normal")}
          </span>
          <span>${(s.totalDeclaredUsdCents / 100).toFixed(2)} / ${((s.customsThresholdUsdCents ?? 22000) / 100).toFixed(0)}</span>
        </div>
      )}

      {/* Pick-list popup (Feature 5). Builds a self-contained printable
          HTML page client-side from the loaded shipment JSON; no extra
          server round-trip + no auth-via-querystring needed. Opens in a
          new window the operator can ⌘P from. */}
      <div style={{ marginBottom: 8 }}>
        <button onClick={() => openPickListPopup(s)}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                         fontSize: 12, color: T.accent, fontWeight: 600 }}>
          {t("admin.ships.printPickList")}
        </button>
      </div>

      <div style={{ fontSize: 12, color: T.textSec, marginBottom: 10 }}>
        {t("admin.ships.itemsCount", { n: s.orders.length })}
        {s.orders.map((o) => (
          <div key={o.id} style={{ paddingLeft: 8, marginTop: 2 }}>
            • {o.postTitle ?? t("admin.ships.deletedItem")}{o.variant ? ` · ${o.variant}` : ""} · {t("admin.ships.itemQty")} {o.qty} · ${(o.totalUsdCents / 100).toFixed(2)}
          </div>
        ))}
      </div>

      {(s.status === "ready" || s.status === "shipped") && (
        <div style={{ background: T.sidebar, padding: 10, borderRadius: 8, marginBottom: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          {/* Tracking inputs — wrap on narrow widths so the carrier + #
              + weight don't squeeze each other off the right edge. */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder={t("admin.ships.tracking.carrier")}
                   style={{ flex: "1 1 100px", minWidth: 0, background: T.bg, color: T.text, border: `1px solid ${T.border}`, padding: "6px 10px", borderRadius: 6, fontSize: 12 }} />
            <input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder={t("admin.ships.tracking.number")}
                   style={{ flex: "2 1 160px", minWidth: 0, background: T.bg, color: T.text, border: `1px solid ${T.border}`, padding: "6px 10px", borderRadius: 6, fontSize: 12 }} />
            <input value={weightKg} onChange={(e) => setWeightKg(e.target.value)} placeholder={t("admin.ships.tracking.weight")} type="number" step="0.1"
                   style={{ flex: "0 0 70px", background: T.bg, color: T.text, border: `1px solid ${T.border}`, padding: "6px 8px", borderRadius: 6, fontSize: 12 }} />
          </div>
          <input value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} placeholder={t("admin.ships.tracking.url")}
                 style={{ background: T.bg, color: T.text, border: `1px solid ${T.border}`, padding: "6px 10px", borderRadius: 6, fontSize: 12 }} />
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button onClick={saveTracking} disabled={saving}
                    style={{ background: `${T.accent}20`, color: T.accent, border: "none", cursor: "pointer", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
              {saving ? t("admin.ships.tracking.saving") : t("admin.ships.tracking.save")}
            </button>
            {s.status === "ready" && (
              <button onClick={markShipped}
                      style={{ background: T.accent, color: "#fff", border: "none", cursor: "pointer", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                {t("admin.ships.markShipped")}
              </button>
            )}
            {s.status === "shipped" && (
              <button onClick={markDelivered}
                      style={{ background: T.online, color: "#fff", border: "none", cursor: "pointer", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                {t("admin.ships.markDelivered")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Pick-list popup (Feature 5) ───
// Builds a standalone printable HTML page from a shipment JSON and
// opens it in a new window. Lives client-side (rather than a server
// endpoint) so it inherits the existing JWT auth without query-string
// token hacks. The popup is self-contained — no external resources —
// so the operator can save-as-PDF if they want a paper trail.
function openPickListPopup(s) {
  const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (ch) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch]
  ));
  const totalDeclaredCents = (s.orders ?? []).reduce(
    (sum, o) => sum + (o.effectiveDeclaredUsdCents ?? o.totalUsdCents), 0,
  );
  const thresholdCents = s.customsThresholdUsdCents ?? 22000;
  const overThreshold = totalDeclaredCents > thresholdCents;
  const recipientBlock = [
    esc(s.recipientName),
    esc(s.recipientPhone),
    esc(s.shipAddress).replace(/\n/g, "<br/>"),
    esc(s.shipCountry),
  ].filter(Boolean).join("<br/>");
  const rows = (s.orders ?? []).map((o) => `
    <tr>
      <td class="check"><input type="checkbox"></td>
      <td>${esc(o.postTitle ?? "(deleted)")}</td>
      <td>${esc(o.variant ?? "")}</td>
      <td class="num">${o.qty}</td>
      <td class="num">$${(o.totalUsdCents / 100).toFixed(2)}</td>
      <td class="num">$${((o.effectiveDeclaredUsdCents ?? o.totalUsdCents) / 100).toFixed(2)}${o.declaredValueUsdCents != null ? " *" : ""}</td>
    </tr>
  `).join("");
  const html = `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>Pick list ${esc(s.id.slice(0, 8))}</title>
<style>
  @page { size: A4; margin: 14mm; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 11pt; color: #111; }
  h1 { font-size: 18pt; margin: 0 0 4pt; }
  .meta { color: #666; font-size: 10pt; margin-bottom: 16pt; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16pt; margin-bottom: 16pt; }
  .card { border: 1pt solid #ccc; padding: 10pt; border-radius: 4pt; }
  .card h3 { margin: 0 0 6pt; font-size: 10pt; text-transform: uppercase; letter-spacing: 0.05em; color: #666; }
  .customs { padding: 8pt 10pt; border-radius: 4pt; margin-bottom: 14pt; background: #f3f3f3; }
  .customs.over { background: #fde7e7; border: 1pt solid #d94545; color: #8a1a1a; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 6pt 4pt; border-bottom: 1pt solid #ddd; text-align: left; vertical-align: top; }
  th { font-size: 9pt; text-transform: uppercase; color: #666; letter-spacing: 0.04em; }
  td.num, th.num { text-align: right; }
  td.check { width: 18pt; }
  td.check input { transform: scale(1.4); }
  .totals { margin-top: 12pt; text-align: right; font-size: 12pt; }
  .totals strong { font-size: 14pt; }
  .footnote { margin-top: 8pt; font-size: 9pt; color: #888; }
  @media print { button { display: none; } }
  button { float: right; padding: 6pt 12pt; font-size: 11pt; cursor: pointer; }
</style></head><body>
<button onclick="window.print()">🖨 Print</button>
<h1>Pick list / Packing slip</h1>
<div class="meta">Shipment #${esc(s.id.slice(0, 8))} · ${new Date((s.createdAt ?? Date.now() / 1000) * 1000).toLocaleDateString()} · Status: ${esc(s.status)}</div>
<div class="grid">
  <div class="card"><h3>Ship to</h3><div>${recipientBlock || "<em>(not set)</em>"}</div></div>
  <div class="card"><h3>Tracking</h3>
    <div>${esc(s.carrier) || "<em>(no carrier)</em>"}<br/>${esc(s.trackingNumber) || "<em>(no tracking)</em>"}</div>
    <div style="margin-top:6pt;font-size:9pt;color:#666;">Weight: ${s.weightKg ?? "—"} kg</div>
  </div>
</div>
<div class="customs ${overThreshold ? "over" : ""}">
  Declared total: <strong>$${(totalDeclaredCents / 100).toFixed(2)}</strong>
  ${overThreshold ? ` — over the 200 EUR threshold ($${(thresholdCents / 100).toFixed(0)}). Split parcel or lower per-item declared values before shipping.` : ""}
</div>
<table>
  <thead><tr><th class="check"></th><th>Item</th><th>Variant</th><th class="num">Qty</th><th class="num">Purchase $</th><th class="num">Declared $</th></tr></thead>
  <tbody>${rows}</tbody>
</table>
<div class="totals">
  ${(s.orders ?? []).length} item${(s.orders ?? []).length === 1 ? "" : "s"} · purchase total
  <strong>$${((s.orders ?? []).reduce((sum, o) => sum + o.totalUsdCents, 0) / 100).toFixed(2)}</strong>
</div>
<div class="footnote">* = customer-overridden declared value (different from purchase price).</div>
</body></html>`;
  const w = window.open("", "_blank", "width=820,height=1080");
  if (!w) return;
  w.document.open(); w.document.write(html); w.document.close();
}

// ─── Payment-methods config sub-components (Feature 1) ───
// Small inline form helpers used inside the Settings tab. Kept here
// rather than in ui.jsx because they're not generic — the label/value
// layout is specific to the payment-methods card.

function PmGroup({ title, children }) {
  return (
    <div style={{ marginBottom: T_NEW.s4 }}>
      <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 8 }}>
        {title}
      </Mono>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}

function PmField({ label, value, onChange, placeholder }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56,
                     fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                     letterSpacing: T_NEW.monoTrack, fontWeight: 600 }}>
        {label}
      </span>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             style={{ background: T_NEW.paper, color: T_NEW.ink,
                      border: `1px solid ${T_NEW.ink12}`,
                      padding: "10px 12px", borderRadius: T_NEW.r1, fontSize: 13,
                      fontFamily: T_NEW.fontMono }} />
    </label>
  );
}

function PmArea({ label, value, onChange, placeholder }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56,
                     fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                     letterSpacing: T_NEW.monoTrack, fontWeight: 600 }}>
        {label}
      </span>
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
                style={{ background: T_NEW.paper, color: T_NEW.ink,
                         border: `1px solid ${T_NEW.ink12}`,
                         padding: "10px 12px", borderRadius: T_NEW.r1, fontSize: 13,
                         fontFamily: T_NEW.fontSans, minHeight: 64, resize: "vertical",
                         lineHeight: 1.5 }} />
    </label>
  );
}

// ─── Stablecoin multi-network editor (USDT / USDC / …) ────────────────
// Each row = { network, address }. Network is free-text (server uppercases
// + dedupes on save). Add-button appends an empty row; trash removes one.
// Empty rows are silently dropped server-side, but we render the placeholder
// so the operator always has at least one editable shell visible.
//
// `title` lets the same component sit next to itself for different coins
// — pass "USDT (multi-network)" or "USDC (multi-network)".
function StablecoinRowEditor({ title, rows, onChange }) {
  const t = useT();
  const display = rows && rows.length > 0 ? rows : [{ network: "", address: "" }];
  const update = (i, patch) => {
    const next = display.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange(next);
  };
  const add = () => onChange([...(rows ?? []), { network: "", address: "" }]);
  const remove = (i) => {
    const next = display.filter((_, idx) => idx !== i);
    onChange(next);
  };
  return (
    <div style={{ marginBottom: T_NEW.s4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    marginBottom: 8, gap: 10 }}>
        <Mono size={T_NEW.micro} color={T_NEW.ink56}>{title}</Mono>
        <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, fontStyle: "italic",
                       fontFamily: T_NEW.fontSerif }}>
          {t("admin.pm.networkHint")}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {display.map((row, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <input
              value={row.network ?? ""}
              onChange={(e) => update(i, { network: e.target.value })}
              placeholder="TRC20"
              style={{ width: 110, background: T_NEW.paper, color: T_NEW.ink,
                       border: `1px solid ${T_NEW.ink12}`,
                       padding: "10px 12px", borderRadius: T_NEW.r1, fontSize: 13,
                       fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                       letterSpacing: T_NEW.monoTrack, fontWeight: 600 }} />
            <input
              value={row.address ?? ""}
              onChange={(e) => update(i, { address: e.target.value })}
              placeholder={t("admin.pm.placeholder.networkAddress")}
              style={{ flex: 1, minWidth: 0, background: T_NEW.paper, color: T_NEW.ink,
                       border: `1px solid ${T_NEW.ink12}`,
                       padding: "10px 12px", borderRadius: T_NEW.r1, fontSize: 13,
                       fontFamily: T_NEW.fontMono }} />
            <button
              type="button"
              onClick={() => remove(i)}
              title={t("admin.pm.removeNetwork")}
              style={{ background: "transparent", color: T_NEW.ink56,
                       border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r1,
                       padding: "0 12px", cursor: "pointer", fontSize: T_NEW.bodyLg,
                       lineHeight: 1, fontFamily: T_NEW.fontMono }}>
              ×
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        style={{ marginTop: 10, background: T_NEW.persimmonSoft, color: T_NEW.persimmon,
                 border: "none", borderRadius: T_NEW.r1, padding: "8px 14px",
                 cursor: "pointer", fontSize: T_NEW.micro, fontWeight: 700,
                 fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                 letterSpacing: T_NEW.monoTrack }}>
        {t("admin.pm.addNetwork")}
      </button>
    </div>
  );
}

// ─── ReceiptPhotos (Feature 4) ───
// Renders existing receipt photo thumbnails + an "Add photo" button.
// Uploads via the existing /api/upload pipeline (sharp, EXIF strip,
// content-addressed storage), then POSTs the resulting path(s) to the
// admin received-photos endpoint. Multi-file upload supported.
function ReceiptPhotos({ order, onChanged }) {
  const toast = useToast();
  const t = useT();
  const [uploading, setUploading] = useState(0);
  const inputRef = useRef(null);
  const urls = order.receivedPhotoUrls ?? [];

  const handle = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).slice(0, 8);
    setUploading(arr.length);
    const paths = [];
    for (const f of arr) {
      try {
        const r = await api.uploadImage(f);
        paths.push(r.path);
      } catch (e) { toast.error(e.message); }
    }
    setUploading(0);
    if (paths.length === 0) return;
    try {
      await api.addReceivedPhotos(order.id, paths);
      toast.success(t("admin.orders.receipt.added", { n: paths.length }));
      onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div style={{ marginTop: 8, padding: "6px 8px", background: `${T.accent}08`, borderRadius: 6 }}>
      <div style={{ fontSize: 11, color: T.textSec, fontWeight: 600, marginBottom: 6 }}>
        {t("admin.orders.receipt.title", { n: urls.length })}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {urls.map((u, i) => (
          <a key={i} href={u} target="_blank" rel="noopener noreferrer">
            <img src={u} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 4, border: `1px solid ${T.border}` }} />
          </a>
        ))}
        <button onClick={() => inputRef.current?.click()} disabled={uploading > 0}
                style={{ width: 56, height: 56, borderRadius: 4, background: T.bg,
                         border: `1.5px dashed ${T.border}`, color: T.textSec,
                         cursor: uploading > 0 ? "wait" : "pointer", fontSize: 11 }}>
          {uploading > 0 ? t("admin.orders.receipt.uploading") : t("admin.orders.receipt.add")}
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: "none" }}
               onChange={(e) => { handle(e.target.files); e.target.value = ""; }} />
      </div>
    </div>
  );
}

// ─── WaRouteRow ───
// One row per WhatsApp sender route. Inline-edit display name, channel,
// and auto-publish toggle. Delete removes the route (so the sender falls
// back to default channel, drafts only).
function WaRouteRow({ route, channels, onChanged }) {
  const toast = useToast();
  const t = useT();
  const [name, setName] = useState(route.displayName ?? "");
  const [channelId, setChannelId] = useState(route.channelId ?? "");
  const [autoPub, setAutoPub] = useState(!!route.autoPublish);
  const [saving, setSaving] = useState(false);
  const dirty = (name || "") !== (route.displayName || "")
             || (channelId || "") !== (route.channelId || "")
             || autoPub !== !!route.autoPublish;
  const save = async () => {
    setSaving(true);
    try {
      await api.updateWaRoute(route.id, {
        displayName: name || null,
        channelId: channelId || null,
        autoPublish: autoPub,
      });
      toast.success(t("admin.wa.route.saved"));
      onChanged?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!await toast.confirm(t("admin.wa.route.delete.confirm", { phone: route.senderPhone }))) return;
    try { await api.deleteWaRoute(route.id); onChanged?.(); toast.success(t("admin.wa.route.deleted")); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div style={{ background: T.sidebar, padding: 10, borderRadius: 8 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "monospace", fontSize: 12, color: T.textSec }}>{route.senderPhone}</div>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("admin.wa.route.displayName")}
               style={{ flex: "1 1 140px", minWidth: 0, background: T.bg, color: T.text, border: `1px solid ${T.border}`, padding: "6px 10px", borderRadius: 6, fontSize: 12 }} />
        <select value={channelId} onChange={(e) => setChannelId(e.target.value)}
                style={{ flex: "1 1 140px", minWidth: 0, background: T.bg, color: T.text, border: `1px solid ${T.border}`, padding: "6px 10px", borderRadius: 6, fontSize: 12 }}>
          <option value="">{t("admin.wa.route.channelDefault")}</option>
          {channels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.text, cursor: "pointer" }}>
          <input type="checkbox" checked={autoPub} onChange={(e) => setAutoPub(e.target.checked)} />
          {t("admin.wa.route.autopublish")}
        </label>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button onClick={del} style={{ background: `${T.danger}20`, color: T.danger, border: "none", cursor: "pointer", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{t("admin.wa.route.delete")}</button>
          <button disabled={!dirty || saving} onClick={save}
                  style={{ background: dirty ? T.accent : T.border, color: "#fff", border: "none", cursor: dirty ? "pointer" : "default", padding: "4px 12px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
            {saving ? t("admin.wa.route.saving") : t("admin.wa.route.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AdminChannelRow ───
// ─── StoreFeePanel — set commission once per STORE (source_retailer) ───
// Instead of editing dozens of channels one by one, the operator sets a
// single % that bulk-applies to every channel of a store. Per-channel
// rows below still allow fine-tuning afterward.
const STORE_LABELS = { costco: "Costco", walmart: "Walmart", "nordstrom-rack": "Nordstrom Rack" };

function StoreFeePanel({ channels, onSaved }) {
  const t = useT();
  const toast = useToast();
  const groups = useMemo(() => {
    const m = new Map();
    for (const ch of channels) {
      const s = ch.sourceRetailer;
      if (!s) continue;
      if (!m.has(s)) m.set(s, []);
      m.get(s).push(ch);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [channels]);
  if (groups.length === 0) return null;
  return (
    <div style={{ marginBottom: 16, padding: 14, background: T.bg, borderRadius: 10, border: `1px solid ${T.border}` }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{t("admin.storeFees.title")}</div>
      <div style={{ fontSize: 12, color: T.textSec, marginBottom: 8, lineHeight: 1.5 }}>{t("admin.storeFees.hint")}</div>
      {groups.map(([store, chs]) => (
        <StoreFeeRow key={store} store={store} channels={chs} onSaved={onSaved} t={t} toast={toast} />
      ))}
    </div>
  );
}

function StoreFeeRow({ store, channels, onSaved, t, toast }) {
  const uniform = (key) => {
    const vals = [...new Set(channels.map((c) => c[key] ?? 0))];
    return vals.length === 1 ? vals[0] : null; // null = mixed
  };
  const curFee = uniform("organizerFeePct");
  const [fee, setFee] = useState(curFee != null ? String(curFee) : "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setFee(curFee != null ? String(curFee) : ""); }, [curFee]);
  const label = STORE_LABELS[store] || store;
  const apply = async () => {
    const pct = parseFloat(fee);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) { toast.error(t("admin.channels.error.fee")); return; }
    setSaving(true);
    try {
      const r = await api.setStoreFees(store, { organizerFeePct: pct });
      toast.success(t("admin.storeFees.applied", { n: r.updated, store: label }));
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: `1px solid ${T.border}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 11, color: T.textSec }}>
          {t("admin.storeFees.count", { n: channels.length })} ·{" "}
          {curFee != null ? `${curFee}%` : t("admin.storeFees.mixed")}
        </div>
      </div>
      <input type="number" min="0" max="100" step="0.5" value={fee}
             onChange={(e) => setFee(e.target.value)} placeholder="%"
             style={{ width: 70, background: T.sidebar, color: T.text, border: `1px solid ${T.border}`,
                      borderRadius: 8, padding: "6px 8px", fontSize: 13 }} />
      <span style={{ color: T.textSec, fontSize: 13 }}>%</span>
      <Btn disabled={saving} onClick={apply}>{t("admin.storeFees.apply")}</Btn>
    </div>
  );
}

// One row per channel; inline-editable organizer fee + cashback %.
// Save button only enabled when something changed; PATCHes only the
// fields that differ (so we don't clobber other future channel
// settings if multiple admins edit concurrently).
function AdminChannelRow({ channel, users, onSaved, onDeleted }) {
  const toast = useToast();
  const t = useT();
  // Fee + cashback (existing behavior — preserved)
  const [fee, setFee] = useState(String(channel.organizerFeePct ?? 0));
  const [cb, setCb] = useState(String(channel.cashbackPct ?? 0));
  const [saving, setSaving] = useState(false);
  // Edit details panel — collapsed by default; expands on click. Holds
  // name, description, and owner dropdown. Save flushes whichever subset
  // changed alongside fee/cashback in a single PATCH.
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(channel.name ?? "");
  const [desc, setDesc] = useState(channel.description ?? "");
  const [owner, setOwner] = useState(channel.createdBy ?? "");
  const [deleting, setDeleting] = useState(false);

  const feeNum = parseFloat(fee);
  const cbNum = parseFloat(cb);
  const feeChanged = Number.isFinite(feeNum) && feeNum !== (channel.organizerFeePct ?? 0);
  const cbChanged = Number.isFinite(cbNum) && cbNum !== (channel.cashbackPct ?? 0);
  const nameChanged = name.trim() !== (channel.name ?? "");
  const descChanged = desc.trim() !== (channel.description ?? "");
  const ownerChanged = owner !== (channel.createdBy ?? "");
  const dirty = feeChanged || cbChanged || nameChanged || descChanged || ownerChanged;

  // Owner dropdown — managers + admins only. Customers can't own channels.
  const eligibleOwners = (users ?? []).filter(
    (u) => u.role === "manager" || u.role === "admin" || u.isAdmin,
  );

  const save = async () => {
    if (!dirty) return;
    if (Number.isFinite(feeNum) && (feeNum < 0 || feeNum > 100)) { toast.error(t("admin.channels.error.fee")); return; }
    if (Number.isFinite(cbNum) && (cbNum < 0 || cbNum > 100)) { toast.error(t("admin.channels.error.cashback")); return; }
    if (nameChanged && name.trim().length < 2) { toast.error(t("admin.channels.error.name")); return; }
    setSaving(true);
    try {
      const patch = {};
      if (feeChanged) patch.organizerFeePct = feeNum;
      if (cbChanged) patch.cashbackPct = cbNum;
      if (nameChanged) patch.name = name.trim();
      if (descChanged) patch.description = desc.trim();
      if (ownerChanged) patch.createdBy = owner;
      await api.updateChannel(channel.id, patch);
      toast.success(t("admin.channels.saved"));
      onSaved?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  // Destructive — type-the-name to confirm. Prevents accidental cascade
  // deletes when a channel has many posts.
  const del = async () => {
    const typed = await toast.prompt(
      t("admin.channels.delete.confirm", { name: channel.name, n: channel.publishedCount }),
      { placeholder: channel.name },
    );
    if (typed === null) return;
    if (typed.trim() !== channel.name) {
      toast.error(t("admin.channels.delete.mismatch"));
      return;
    }
    setDeleting(true);
    try {
      await api.deleteChannel(channel.id);
      toast.success(t("admin.channels.delete.ok", { name: channel.name }));
      onDeleted?.();
    } catch (e) { toast.error(e.message); }
    finally { setDeleting(false); }
  };

  // Net margin = fee minus cashback. Inline sanity check.
  const netMargin = Number.isFinite(feeNum) && Number.isFinite(cbNum) ? feeNum - cbNum : null;
  const netNegative = netMargin != null && netMargin < 0;

  return (
    <div style={{ background: T_NEW.paperSoft, padding: 14, borderRadius: T_NEW.r2,
                  border: `1px solid ${T_NEW.ink12}` }}>
      {/* Header — channel name (Serif) + slug/count meta, plus action row */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                    gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: T_NEW.fontSerif, fontSize: T_NEW.bodyLg, fontWeight: 500,
                        color: T_NEW.ink, lineHeight: 1.25 }}>
            {channel.name}
          </div>
          <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 3,
                        fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                        letterSpacing: T_NEW.monoTrack }}>
            #{channel.slug} · {t("admin.channels.posts", { n: channel.publishedCount })}
          </div>
          {channel.description && !editing && (
            <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, marginTop: 6,
                          fontFamily: T_NEW.fontSerif, fontStyle: "italic", lineHeight: 1.5 }}>
              {channel.description}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button onClick={() => setEditing((x) => !x)}
                  title={editing ? t("admin.channels.editTitle.collapse") : t("admin.channels.editTitle.expand")}
                  style={{ background: "transparent", color: T_NEW.ink56,
                           border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r1,
                           padding: "6px 12px", cursor: "pointer",
                           fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro,
                           textTransform: "uppercase", letterSpacing: T_NEW.monoTrack,
                           fontWeight: 600 }}>
            {editing ? t("admin.channels.close") : t("admin.channels.edit")}
          </button>
          <button onClick={del} disabled={deleting}
                  title={t("admin.channels.deleteTitle")}
                  style={{ background: deleting ? T_NEW.dangerSoft : "transparent",
                           color: T_NEW.danger,
                           border: `1px solid ${T_NEW.danger}40`, borderRadius: T_NEW.r1,
                           padding: "6px 12px", cursor: deleting ? "default" : "pointer",
                           fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro,
                           textTransform: "uppercase", letterSpacing: T_NEW.monoTrack,
                           fontWeight: 600, opacity: deleting ? 0.6 : 1 }}>
            {deleting ? t("admin.channels.deleting") : t("admin.channels.delete")}
          </button>
        </div>
      </div>

      {/* Inline edit-details panel — name, description, owner. Hidden by
          default; expand via the Edit button. Stays on the same card so
          the operator can see fee/cashback context while editing. */}
      {editing && (
        <div style={{ background: T_NEW.paper, padding: 14, borderRadius: T_NEW.r1,
                      border: `1px solid ${T_NEW.ink12}`, marginBottom: 12,
                      display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 4 }}>
              {t("admin.channels.name")}
            </Mono>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder={t("admin.channels.name.placeholder")}
                   style={{ width: "100%", background: T_NEW.paper, color: T_NEW.ink,
                            border: `1px solid ${T_NEW.ink12}`, padding: "10px 12px",
                            borderRadius: T_NEW.r1, fontSize: 14, fontFamily: T_NEW.fontSerif }} />
          </div>
          <div>
            <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 4 }}>
              {t("admin.channels.description")}
            </Mono>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)}
                      placeholder={t("admin.channels.description.placeholder")}
                      style={{ width: "100%", background: T_NEW.paper, color: T_NEW.ink,
                               border: `1px solid ${T_NEW.ink12}`, padding: "10px 12px",
                               borderRadius: T_NEW.r1, fontSize: 13, minHeight: 56,
                               resize: "vertical", fontFamily: T_NEW.fontSans, lineHeight: 1.5 }} />
          </div>
          <div>
            <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 4 }}>
              {t("admin.channels.owner")}
            </Mono>
            <select value={owner} onChange={(e) => setOwner(e.target.value)}
                    style={{ width: "100%", background: T_NEW.paper, color: T_NEW.ink,
                             border: `1px solid ${T_NEW.ink12}`, padding: "10px 12px",
                             borderRadius: T_NEW.r1, fontSize: 14, fontFamily: T_NEW.fontSans }}>
              {/* Render current owner first even if they're no longer
                  in the eligible list (e.g., demoted to customer) — so
                  the dropdown doesn't silently switch the owner on us. */}
              {!eligibleOwners.some((u) => u.uid === channel.createdBy) && channel.createdBy && (
                <option value={channel.createdBy}>
                  {t("admin.channels.owner.current", { uid: channel.createdBy.slice(0, 8) })}
                </option>
              )}
              {eligibleOwners.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {u.displayName || u.email} · {u.role || (u.isAdmin ? "admin" : "manager")}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Fee + cashback editor — original behavior preserved */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 110px", minWidth: 0 }}>
          <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 4 }}>
            {t("admin.channels.fee")}
          </Mono>
          <input type="number" min="0" max="100" step="0.5" value={fee} onChange={(e) => setFee(e.target.value)}
                 style={{ width: "100%", background: T_NEW.paper, color: T_NEW.ink,
                          border: `1px solid ${T_NEW.ink12}`, padding: "8px 10px",
                          borderRadius: T_NEW.r1, fontSize: 14, fontFamily: T_NEW.fontMono }} />
        </div>
        <div style={{ flex: "1 1 110px", minWidth: 0 }}>
          <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 4 }}>
            {t("admin.channels.cashback")}
          </Mono>
          <input type="number" min="0" max="100" step="0.5" value={cb} onChange={(e) => setCb(e.target.value)}
                 style={{ width: "100%", background: T_NEW.paper, color: T_NEW.ink,
                          border: `1px solid ${T_NEW.ink12}`, padding: "8px 10px",
                          borderRadius: T_NEW.r1, fontSize: 14, fontFamily: T_NEW.fontMono }} />
        </div>
        <Btn disabled={!dirty || saving || netNegative} onClick={save}>
          {saving ? t("admin.channels.saving") : dirty ? t("admin.channels.save") : t("admin.channels.savedShort")}
        </Btn>
      </div>
      {netMargin != null && (
        <div style={{ fontSize: T_NEW.micro, marginTop: 8,
                      color: netNegative ? T_NEW.danger : T_NEW.ink56,
                      fontFamily: T_NEW.fontSans }}>
          {t("admin.channels.netMargin")} <strong style={{ color: netNegative ? T_NEW.danger : T_NEW.persimmon }}>
            {netMargin.toFixed(1)}%
          </strong>
          {netNegative && t("admin.channels.netNegative")}
        </div>
      )}
    </div>
  );
}

// ─── Group + Topic editor rows ───────────────────────────────────────
//
// Inline edit pattern: click "Edit" → name + description go editable;
// topics edit per-row via a pencil icon. Save / Cancel return to view
// mode. No extra modal — admin can rename in seconds without losing
// scroll position.
function GroupRow({ g, t, onRefresh, onAddTopic, toast }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(g.name || "");
  const [desc, setDesc] = useState(g.desc || g.description || "");
  const [saving, setSaving] = useState(false);

  const beginEdit = () => {
    setName(g.name || "");
    setDesc(g.desc || g.description || "");
    setEditing(true);
  };
  const cancel = () => setEditing(false);
  const save = async () => {
    if (!name.trim()) { toast.error(t("admin.groups.error.nameRequired")); return; }
    setSaving(true);
    try {
      await api.updateGroup(g.id, { name: name.trim(), description: desc.trim() });
      toast.success(t("admin.groups.saved"));
      setEditing(false);
      onRefresh();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ background: T.bg, borderRadius: 12, padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                    marginBottom: 10, gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder={t("admin.groups.namePlaceholder")}
                     autoFocus
                     style={{ background: T_NEW.paper, color: T_NEW.ink,
                              border: `1px solid ${T_NEW.ink12}`,
                              padding: "8px 10px", borderRadius: 6,
                              fontSize: 14, fontWeight: 700 }} />
              <textarea value={desc} onChange={(e) => setDesc(e.target.value)}
                        rows={2}
                        placeholder={t("admin.groups.descPlaceholder")}
                        style={{ background: T_NEW.paper, color: T_NEW.ink,
                                 border: `1px solid ${T_NEW.ink12}`,
                                 padding: "8px 10px", borderRadius: 6,
                                 fontSize: 12, resize: "vertical",
                                 fontFamily: T_NEW.fontSans }} />
            </div>
          ) : (
            <>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{g.name}</div>
              {(g.desc || g.description) && (
                <div style={{ fontSize: 12, color: T.textSec, marginTop: 2 }}>
                  {g.desc || g.description}
                </div>
              )}
            </>
          )}
          <div style={{ fontSize: 12, color: T.textSec, marginTop: 4 }}>
            {t("admin.groups.members", { n: g.members.length })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {editing ? (
            <>
              <button onClick={save} disabled={saving}
                      style={{ background: T.accent, color: "#fff", border: "none",
                               cursor: "pointer", padding: "4px 10px",
                               borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                {saving ? "…" : t("admin.groups.save")}
              </button>
              <button onClick={cancel}
                      style={{ background: "none", border: `1px solid ${T.border}`,
                               cursor: "pointer", padding: "4px 10px",
                               borderRadius: 6, fontSize: 12, color: T.text }}>
                {t("admin.groups.cancel")}
              </button>
            </>
          ) : (
            <>
              <button onClick={beginEdit}
                      style={{ background: `${T.accent}20`, border: "none",
                               cursor: "pointer", padding: "4px 10px",
                               borderRadius: 6, color: T.accent, fontSize: 12, fontWeight: 600 }}>
                {t("admin.groups.edit")}
              </button>
              <button onClick={() => onAddTopic(g.id)}
                      style={{ background: `${T.accent}20`, border: "none",
                               cursor: "pointer", padding: "4px 10px",
                               borderRadius: 6, color: T.accent, fontSize: 12, fontWeight: 600 }}>
                {t("admin.groups.addTopic")}
              </button>
              <button onClick={async () => {
                        if (!confirm(t("admin.groups.deleteConfirm", { name: g.name }))) return;
                        try { await api.deleteGroup(g.id); onRefresh(); }
                        catch (e) { toast.error(e.message); }
                      }}
                      style={{ background: `${T.danger}20`, border: "none",
                               cursor: "pointer", padding: "4px 10px",
                               borderRadius: 6, color: T.danger, fontSize: 12, fontWeight: 600 }}>
                {t("admin.groups.delete")}
              </button>
            </>
          )}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {(g.topics || []).map((tp) => (
          <TopicRow key={tp.id} groupId={g.id} topic={tp}
                    canDelete={(g.topics || []).length > 1}
                    t={t} onRefresh={onRefresh} toast={toast} />
        ))}
      </div>
    </div>
  );
}

function TopicRow({ groupId, topic, canDelete, t, onRefresh, toast }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(topic.name || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error(t("admin.groups.error.topicNameRequired")); return; }
    setSaving(true);
    try {
      await api.updateTopic(groupId, topic.id, name.trim());
      toast.success(t("admin.groups.topic.saved"));
      setEditing(false);
      onRefresh();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "6px 12px", background: T.topicBg, borderRadius: 8, gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
        {I.hash()}
        {editing ? (
          <input value={name} onChange={(e) => setName(e.target.value)}
                 autoFocus
                 onKeyDown={(e) => {
                   if (e.key === "Enter") save();
                   if (e.key === "Escape") { setName(topic.name || ""); setEditing(false); }
                 }}
                 style={{ flex: 1, minWidth: 0,
                          background: T_NEW.paper, color: T_NEW.ink,
                          border: `1px solid ${T_NEW.ink12}`,
                          padding: "4px 8px", borderRadius: 4,
                          fontSize: 14, fontFamily: T_NEW.fontSans }} />
        ) : (
          <span style={{ fontSize: 14 }}>{topic.name}</span>
        )}
      </div>
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        {editing ? (
          <>
            <button onClick={save} disabled={saving}
                    style={{ background: T.accent, color: "#fff", border: "none",
                             cursor: "pointer", padding: "3px 8px",
                             borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
              {saving ? "…" : t("admin.groups.save")}
            </button>
            <button onClick={() => { setName(topic.name || ""); setEditing(false); }}
                    style={{ background: "none", border: "none", cursor: "pointer",
                             padding: "3px 8px", color: T.textSec, fontSize: 11 }}>
              {t("admin.groups.cancel")}
            </button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)}
                    style={{ background: "none", border: "none", cursor: "pointer",
                             padding: 2, display: "flex", color: T.accent, fontSize: 13 }}
                    title={t("admin.groups.topic.edit")}>
              ✎
            </button>
            {canDelete && (
              <button onClick={async () => {
                        if (!confirm(t("admin.groups.topic.deleteConfirm", { name: topic.name }))) return;
                        try { await api.deleteTopic(groupId, topic.id); onRefresh(); }
                        catch (e) { toast.error(e.message); }
                      }}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex" }}
                      title={t("admin.groups.topic.delete")}>
                {I.trash()}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
